import React, { useRef, useState, useEffect } from 'react';

import moment from 'moment-timezone';
import update from 'immutability-helper';

import API from 'files/api.js';
import Abstract from 'classes/Abstract.js';
import AltFieldMapper, { validateRequiredFields } from 'views/AltFieldMapper.js';
import Appearance from 'styles/Appearance.js';
import { CardElement, Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import Content from 'managers/Content.js';
import CreditsPicker from 'views/CreditsPicker.js';
import CustomListField from 'views/CustomListField.js';
import DateDurationPickerField from 'views/DateDurationPickerField.js';
import Client from 'classes/Client.js';
import Appointment from 'classes/Appointment.js';
import { AppointmentDetails } from 'managers/Appointments.js';
import Event from 'classes/Event.js';
import { EventDetails } from 'managers/Clients.js';
import FieldMapper, { formatFields } from 'views/FieldMapper.js';
import Layer, { LayerItem } from 'structure/Layer.js';
import LottieView from 'views/Lottie.js';
import Notification from 'classes/Notification.js';
import PageControl from 'views/PageControl.js';
import Panel from 'structure/Panel.js';
import Payment from 'classes/Payment.js';
import Lead from 'classes/Lead.js';
import Request from 'files/Request.js';
import SystemEvent from 'classes/SystemEvent.js';
import TableListHeader from 'views/TableListHeader.js';
import TextField, { getIcon } from 'views/TextField.js';
import OutboundCall from 'classes/OutboundCall.js';
import { OutboundCallDetails, LeadDetails } from 'managers/Leads.js';
import User from 'classes/User.js';
import UserAvailability from 'classes/UserAvailability.js';
import UserLookupField from 'views/UserLookupField.js';
import Utils from 'files/Utils.js';
import Views, { AltBadge } from 'views/Main.js';

export const AddEditUser = ({ isNewTarget, level }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? 'new_user' : `edit_user_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);

    const onSubmitClick = async () => {
        try {

            // check that all required fields have been filled out
            setLoading('submit');
            await validateRequiredFields(getFields);

            // submit request to server
            await Request.post(utils, '/users/', {
                ...user,
                client: null,
                type: isNewTarget ? 'new' : 'update',
                user_id: isNewTarget === false && abstract.getID()
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: `Account ${isNewTarget ? 'Created' : 'Updated'}`,
                message: `The user account for ${user.first_name} ${user.last_name} has been ${isNewTarget ? 'created' : 'updated'}`,
                onShow: () => {

                    // close layer and notify subscribers of account update
                    setLayerState('close');
                    utils.content.fetch('users');

                    // notify subscribers of new account data if applicable
                    if(isNewTarget === false) {
                        utils.content.update({
                            object: {
                                ...abstract.object,
                                ...user
                            },
                            type: 'users'
                        });
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this account. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateTarget = props => {
        setUser({ ...user, ...props });
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: isNewTarget ? 'Submit' : 'Save Changes'
        }];
    }

    const getContent = () => {
        if(!user) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: 100,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 40,
                        width: 40
                    }}/>
                </div>
            )
        }
        return (
            <AltFieldMapper
            fields={getFields()} 
            utils={utils}/>
        )
    }

    const getFields = () => {
        return [{
            key: 'details',
            title: 'Details',
            items: [{
                component: 'textfield',
                key: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text }),
                title: 'First Name',
                value: user.first_name
            },{
                component: 'textfield',
                key: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text }),
                title: 'Last Name',
                value: user.last_name
            }]
        },{
            key: 'account',
            title: 'Account',
            items: [{
                component: 'list',
                items: levels,
                key: 'level',
                onChange: item => onUpdateTarget({ level: item?.id }),
                title: 'Type',
                value: user.level ? User.levels.toText(user.level) : null
            },{
                component: 'textfield',
                key: 'username',
                onChange: text => onUpdateTarget({ username: text }),
                title: 'Username',
                value: user.username
            },{
                component: 'textfield',
                key: 'password',
                onChange: text => onUpdateTarget({password: text}),
                props: {
                    isSecure: true,
                    useValidator: true
                },
                required: isNewTarget,
                title: 'Password',
                value: user.password,
                visible: isNewTarget
            }]
        },{
            key: 'contact',
            title: 'Contact Information',
            items: [{
                component: 'textfield',
                key: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: user.email_address
            },{
                component: 'textfield',
                key: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                required: false,
                title: 'Phone Number',
                value: user.phone_number
            },{
                component: 'textfield',
                key: 'slack_member_id',
                onChange: text => {
                    onUpdateTarget({ 
                        preferences: {
                            ...user.preferences,
                            slack_member_id: text
                        }
                    });
                },
                required: false,
                title: 'Slack Member ID',
                value: user.preferences?.slack_member_id
            }]
        },{
            key: 'location',
            title: 'Location',
            items: [{
                component: 'address_lookup',
                key: 'address',
                onChange: result => onUpdateTarget(result),
                required: false,
                title: 'Address',
                value: user.address && {
                    address: user.address,
                    location: user.location
                }
            },{
                component: 'timezone_lookup',
                key: 'timezone',
                onChange: zone => onUpdateTarget({ timezone: zone }),
                title: 'Timezone',
                value: user.timezone
            }]
        }];
    }

    const setupTarget = async () => {
        try {

            // setup account type list items
            let codes = User.levels.get();
            let levels = Object.values(codes).filter(code => {
                return code !== codes.system_admin;
            }).map(code => ({
                id: code,
                title: User.levels.toText(code)
            })).sort((a,b) => {
                return a.title.localeCompare(b.title);
            });
            setLevels(levels);

            // setup default values for editing flow
            setUser({ 
                ...abstract.object,
                level: level || abstract.object.level,
                phone_number: abstract.object.phone_number ? Utils.formatPhoneNumber(abstract.object.phone_number) : null,
                ...isNewTarget === true && {
                    client_id: utils.client.get().id
                }
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue setting up the editing process for this account. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close')
            });
        }
    }

    useEffect(() => {
        setTimeout(setupTarget, 500);
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={isNewTarget ? 'New User Account' : abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            {getContent()}
        </Layer>
    )
}

export const Notifications = ({ index, options, utils }) => {

    const panelID = 'notifications';
    const limit = 15;

    const [loading, setLoading] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [offset, setOffset] = useState(0);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            let { notifications, paging } = await Request.get(utils, '/notifications/', {
                type: 'all',
                limit: limit,
                offset: offset,
                search_text: searchText
            });

            setLoading(false);
            setPaging(paging);
            setNotifications(notifications.map(notification => Notification.create(notification)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the notifications list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onNotificationClick = notification => {
        utils.layer.open({
            id: `notification_details_${notification.id}`,
            abstract: Abstract.create({
                type: 'notification',
                object: notification
            }),
            Component: NotificationDetails
        })
    }

    const getContent = () => {
        if(notifications.length === 0) {
            return (
                Views.entry({
                    title: `No Notifications Found`,
                    subTitle: `No notifications have been sent to your account`,
                    bottomBorder: false,
                    hideIcon: true
                })
            )
        }
        return notifications.map((notification, index) => {
            return (
                Views.entry({
                    key: index,
                    title: notification.title,
                    subTitle: notification.message,
                    icon: {
                        path: notification.from_user ? notification.from_user.avatar : 'images/push-notification-icon-white.png',
                        style: {
                            borderRadius: '50%',
                            overflow: 'hidden'
                        },
                        imageStyle: {
                            backgroundColor: getNotificationIconColor(notification)
                        }
                    },
                    bottomBorder: true,
                    onClick: onNotificationClick.bind(this, notification)
                })
            )
        })
    }

    useEffect(() => {
        fetchNotifications();
    }, [offset, searchText]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchNotifications);
        utils.content.subscribe(panelID, ['notifications'], {
            onFetch: fetchNotifications
        });
        return () => {
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', fetchNotifications);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'Notifications'}
        utils={utils}
        options={{
            ...options,
            loading: loading,
            search: {
                placeholder: 'Search by title or message...',
                onChange: text => setSearchText(text)
            },
            paging: {
                data: paging,
                limit: limit,
                offset: offset,
                onClick: next => setOffset(next)
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const SystemEvents = ({ index, options, utils }) => {

    const panelID = 'system_events';
    const limit = 15;

    const [loading, setLoading] = useState(null);
    const [events, setEvents] = useState([]);
    const [offset, setOffset] = useState(0);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);

    const onEventClick = evt => {
        utils.layer.open({
            id: `system_event_details_${evt.id}`,
            abstract: Abstract.create({
                type: 'system_event',
                object: evt
            }),
            Component: SystemEventDetails
        });
    }

    const getContent = () => {
        if(events.length === 0) {
            return (
                Views.entry({
                    title: `No System Events Found`,
                    subTitle: `No system events were found for your client workspace.`,
                    bottomBorder: false,
                    hideIcon: true
                })
            )
        }
        return events.map((evt, index) => {
            let { values } = getSystemEventProps(evt);
            return (
                Views.entry({
                    key: index,
                    title: evt.title,
                    subTitle: values.length > 0 ? `${values[0]}${values.length > 1 ? ` and ${values.length - 1} other ${values.length - 1 === 1 ? 'change':'changes'}` : ''}` : 'No overview available',
                    badge: getSystemEventBadges(evt),
                    icon: {
                        path: evt.user.avatar
                    },
                    bottomBorder: true,
                    onClick: onEventClick.bind(this, evt)
                })
            )
        });
    }

    const connectToSockets = async () => {
        try {
            await utils.sockets.on('system', 'on_new_event', fetchEvents);
        } catch(e) {
            console.error(e.message)
        }
    }

    const disconnectFromSockets = async () => {
        try {
            await utils.sockets.off('system', 'on_new_event', fetchEvents);
        } catch(e) {
            console.error(e.message)
        }
    }

    const fetchEvents = async () => {
        try {
            setLoading(true);
            let { events, paging } = await Request.get(utils, '/utils/', {
                limit: limit,
                offset: offset,
                search_text: searchText,
                type: 'system_events'
            });

            setLoading(false);
            setPaging(paging);
            setEvents(events.map(evt => SystemEvent.create(evt)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the system events list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchEvents();
    }, [offset, searchText]);

    useEffect(() => {

        connectToSockets();

        // setup client on change listener
        utils.events.on(panelID, 'client_change', fetchEvents);

        // subscribe to system event updates
        utils.content.subscribe(panelID, ['system_events'], {
            onFetch: fetchEvents
        });

        return () => {
            disconnectFromSockets();
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', fetchEvents);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'System Events'}
        utils={utils}
        options={{
            ...options,
            loading: loading,
            search: {
                placeholder: 'Search by first or last name...',
                onChange: text => setSearchText(text)
            },
            paging: {
                data: paging,
                limit: limit,
                offset: offset,
                onClick: next => setOffset(next)
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const SystemEventsLayerItem = ({ abstract, permissions = [], utils }) => {

    const limit = 5;
    const offset = useRef(0);

    const [events, setEvents] = useState([]);
    const [hasFetchedEvents, setHasFetchedEvents] = useState(false);
    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);

    const onSystemEventClick = evt => {
        utils.layer.open({
            abstract: Abstract.create({
                type: 'system_event',
                object: evt
            }),
            Component: SystemEventDetails,
            id: `system_event_details_${evt.id}`
        });
    } 

    const getBadgeColor = title => {
        switch(title) {
            case 'Created':
            return Appearance.colors.primary();

            case 'Deleted':
            return Appearance.colors.red;

            case 'Note':

            case 'Updated':
            return Appearance.colors.secondary();

            case 'Warning':
            return Appearance.colors.yellow;

            default:
            return Appearance.colors.grey();
        }
    }
    
    const getContent = () => {

        // prevent moving forward if feature is disabled for current user
        let match = permissions.find(key => utils.user.permissions.get(key) === false);
        if(match) {
            return null;
        }

        // return a loading component if applicable
        if(loading === true) {
            return (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 15,
                    width: '100%'
                }}>
                    <LottieView
                    loop={true}
                    autoPlay={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }

        // return a placeholder if events fetch has not been requested
        if(hasFetchedEvents === false) {
            return (
                <div 
                className={'text-button'}
                onClick={fetchEvents}
                style={{
                    ...Appearance.styles.unstyledPanel(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 24,
                    width: '100%'
                }}>
                    <img 
                    src={window.theme === 'dark' ? 'images/lead-panel-click-white.png' : 'images/lead-panel-click-grey.png'}
                    style={{
                        height: 50,
                        marginBottom: 8,
                        objectFit: 'contain',
                        width: 50
                    }} />
                    <span style={{
                        ...Appearance.textStyles.title(),
                        marginBottom: 2,
                        textAlign: 'center'
                    }}>{'Click to Load System Events'}</span>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        textAlign: 'center',
                        whiteSpace: 'wrap'
                    }}>{'We automatically hide the system events list to improve the overall performance.'}</span>
                </div>
            )
        }

        // return no events found if applicable
        if(events.length === 0) {
            return (   
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {Views.entry({
                        bottomBorder: false,
                        subTitle: 'No system events for this resource were found.',
                        title: 'No Events Found'
                    })}
                </div> 
            )
        }

        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {events.map((evt, index) => {
                    let { title, values } = getSystemEventProps(evt);
                    if(!values) {
                        return null;
                    }
                    return (
                        Views.entry({
                            bottomBorder: index !== events.length - 1,
                            icon: {
                                path: evt.user.avatar
                            },
                            key: index,
                            onClick: onSystemEventClick.bind(this, evt),
                            rightContent: (
                                <AltBadge 
                                content={{
                                    color: getBadgeColor(title),
                                    text: title
                                }} 
                                style={{
                                    flex: 'none'
                                }} />
                            ),
                            subTitle: values.length > 0 ? `${values[0]}${values.length > 1 ? ` and ${values.length - 1} other ${values.length - 1 === 1 ? 'change':'changes'}` : ''}` : 'No overview available',
                            title: Utils.formatDate(evt.date)
                        })
                    )
                })}
                {paging && (
                    <PageControl
                    data={paging}
                    limit={limit}
                    offset={offset.current}
                    onClick={next => {
                        setLoading(true);
                        offset.current = next;
                        fetchEvents();
                    }} />
                )}
            </div>
        )
    }

    const fetchEvents = async () => {
        try {

            setLoading(true);
            let { events, paging } = await Request.get(utils, '/utils/', {
                limit: limit,
                offset: offset.current,
                target_id: abstract.getID(),
                target_type: abstract.type,
                type: 'system_events'
            });

            setLoading(false);
            setPaging(paging);
            setHasFetchedEvents(true);
            setEvents(events.map(evt => SystemEvent.create(evt)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the system events list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    return (
        <LayerItem 
        collapsed={false}
        lastItem={true}
        title={'System Events'}>
            {getContent()}
        </LayerItem>
    )
}

export const Users = ({ level, panelID, shortName, title }, { index, options, utils }) => {

    const limit = 15;
    const offset = useRef(0);
    const showInactive = useRef(false);
    const sorting = useRef(null);

    const [loading, setLoading] = useState(null);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [users, setUsers] = useState([]);

    const onNewUser = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: User.new(),
                type: 'users'
            }),
            Component: AddEditUser.bind(this, { isNewTarget: true }),
            id: 'new_user'
        });
    }

    const onPrintUsers = async props => {
        return new Promise(async (resolve, reject) => {
            try {
                setLoading(true);
                let { users } = await Request.get(utils, '/users/', {
                    type: 'all',
                    levels: [level],
                    ...sorting.current,
                    ...props
                });

                setLoading(false);
                resolve(users.map(user => User.create(user)));

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        })
    }

    const onUserClick = user => {
        utils.layer.open({
            abstract: Abstract.create({
                object: user,
                type: 'users'
            }),
            Component: UserDetails,
            id: `user_details_${user.user_id}`,
            permissions: ['users.details']
        });
    }

    const getButtons = () => {
        return [{
            key: 'new',
            title: `New ${shortName}`,
            style: 'default',
            onClick: onNewUser,
            visible: utils.user.get().level <= User.levels.get().sales_lead
        },{
            key: 'active',
            title: `${showInactive.current ? 'Hide' : 'Show'} Inactive`,
            style: showInactive.current ? 'default' : 'grey',
            onClick: () => {
                offset.current = 0;
                showInactive.current = !showInactive.current;
                fetchUsers();
            }
        }]
    }

    const getContent = () => {
        if(users.length === 0) {
            return (
                Views.entry({
                    title: `No ${title} Found`,
                    subTitle: `No ${title} were found in the system`,
                    bottomBorder: false,
                    hideIcon: true
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {users.map(getFields)}
                </tbody>
            </table>
        )
    }

    const getFields = (user, index, users) => {

        let target = user || {};
        let fields = [{
            key: 'full_name',
            title: 'Name',
            icon: target.avatar,
            value: target.full_name
        },{
            key: 'email_address',
            sortable: false,
            title: 'Email Address',
            value: target.email_address
        },{
            key: 'phone_number',
            sortable: false,
            title: 'Phone Number',
            value: target.phone_number ? Utils.formatPhoneNumber(target.phone_number) : 'N/A'
        }];

        if(level !== User.levels.get().customer) {
            fields = fields.concat([{
                key: 'last_login',
                title: 'Last Login',
                value: target.last_login ? moment(target.last_login).format('MM/DD/YYYY [at] h:mma') : 'No Logins Found'
            }])
        }

        // create table headers with custom sorting options
        // conform external sort to match internal header sort if applicable
        if(!user) {
            return (
                <TableListHeader
                fields={fields}
                onChange={props => {
                    sorting.current = props;
                    fetchUsers();
                }}
                value={sorting.current}/>
            )
        }

        // loop through result rows
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: index !== users.length - 1 && `1px solid ${Appearance.colors.divider()}`
            }}>
            {fields.map((field, index) => {
                return (
                    <td
                    key={index}
                    className={'pl-2 pr-3 py-2 flexible-table-column'}
                    onClick={onUserClick.bind(this, user)}>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row'
                        }}>
                            {typeof(field.icon) === 'string' && (
                                <img
                                src={field.icon}
                                style={{
                                    borderRadius: 12.5,
                                    height: 25,
                                    minHeight: 25,
                                    minWidth: 25,
                                    objectFit: 'cover',
                                    overflow: 'hidden',
                                    marginRight: 8,
                                    width: 25
                                }}/>
                            )}
                            <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                        </div>
                    </td>
                )
            })}
            </tr>
        )
    }

    const getPrintProps = () => {

        let headers = [{
            key: 'full_name',
            title: 'Name'
        },{
            key: 'email_address',
            title: 'Email'
        },{
            key: 'phone_number',
            title: 'Phone'
        }];
        if(level !== User.levels.get().customer) {
            headers = headers.concat([{
                key: 'last_login',
                title: 'Last Login'
            }])
        }

        return {
            headers: headers,
            onFetch: onPrintUsers,
            onRenderItem: (item, index, items) => ({
                full_name: item.full_name,
                email_address: item.email_address,
                phone_number: item.phone_number,
                next_appointment: item.next_appointment ? moment(item.next_appointment).format('MM/DD/YYYY [at] h:mma') : 'None Scheduled',
                last_login: item.last_login ? moment(item.last_login).format('MM/DD/YYYY [at] h:mma') : 'No Logins Found'
            })
        }
    }

    const fetchUsers = async () => {
        try {
            setLoading(true);
            let { users, paging } = await Request.get(utils, '/users/', {
                levels: [level],
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                show_inactive: showInactive.current,
                type: 'all',
                ...sorting.current
            });

            setLoading(false);
            setPaging(paging);
            setUsers(users.map(user => User.create(user)))

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the users list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        fetchUsers();
    }, [searchText]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchUsers);
        utils.content.subscribe(panelID, ['users'], {
            onFetch: fetchUsers,
            onUpdate: abstract => {
                setUsers(users => {
                    return users.map(user => {
                        return user.user_id === abstract.getID() ? abstract.object : user
                    })
                })
            }
        });
        return () => {
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', fetchUsers);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={title}
        utils={utils}
        options={{
            ...options,
            loading: loading,
            search: {
                placeholder: 'Search by first or last name...',
                onChange: setSearchText
            },
            print: getPrintProps(),
            buttons: getButtons(),
            paging: {
                data: paging,
                limit: limit,
                offset: offset.current,
                onClick: next => {
                    offset.current = next;
                    fetchUsers();
                }
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}


export const NotificationDetails = ({ abstract, index, options, utils }) => {

    const layerID = `notification_details_${abstract.getID()}`;

    const getFields = () => {

        let notification = abstract.object;
        return [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'id',
                title: 'ID',
                value: notification.id
            },{
                key: 'date',
                title: 'Date',
                value: Utils.formatDate(notification.date)
            },{
                key: 'title',
                title: 'Title',
                value: notification.title
            },{
                key: 'description',
                title: 'Message',
                value: notification.message
            }],
            style: {
                marginBottom: 0
            }
        }];
    }

    return (
        <Layer
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            sizing: 'medium',
        }}>
            <FieldMapper 
            sections={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const PaymentsOverview = ({ abstract, index, options, utils }) => {

    const layerID = `payments_overview_${abstract.getID()}`;
    const stripeProps = useRef({});

    const [clientPayments, setClientPayments] = useState(null);
    const [clientPaymentsLimit, setClientPaymentsOLimit] = useState(5);
    const [clientPaymentsOffset, setClientPaymentsOffset] = useState(0);
    const [clientPaymentsPaging, setClientPaymentsPaging] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [methods, setMethods] = useState(null);
    const [newMethodCompleted, setNewMethodCompleted] = useState(false);
    const [userPayments, setUserPayments] = useState(null);
    const [userPaymentsLimit, setUserPaymentsLimit] = useState(5);
    const [userPaymentsOffset, setUserPaymentsOffset] = useState(0);
    const [userPaymentsPaging, setUserPaymentsPaging] = useState(null);

    const onAddNewMethod = async () => {
        try {
            setLoading(true);
            let element = stripeProps.current.elements.getElement(CardElement);
            let { token } = await stripeProps.current.stripe.createToken(element);

            let { method } = await Request.post(utils, '/payments/', {
                source_category: Payment.Method.source_categories.get().user,
                source_id: token.card.id,
                token: token.id,
                type: 'new_method'
            });

            setLoading(false);
            element.clear();

            let nextMethod = Payment.Method.create(method);
            setMethods(methods => update(methods, {
                $unshift: [ nextMethod ]
            }));

            // notify subscribers that payment methods have changed
            utils.events.emit('payment_method_change');

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue adding your payment method. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onCreditsPurchased = amount => {

        setUserPaymentsOffset(0);
        setClientPaymentsOffset(0);
        fetchUserPayments();
        fetchClientPayments();
        utils.banner.show({
            title: 'Credits Purchased',
            message: `Your purchase of ${Utils.toCurrency(amount)} for credits has been completed`
        })
    }

    const onMethodAdded = method => {
        setMethods(methods => update(methods, {
            $push: [ method ]
        }))
    }

    const onMethodRemoved = method => {
        setMethods(methods => {
            return methods.filter(prev_method => {
                return prev_method.id !== method.id;
            })
        })
    }

    const onPaymentClick = () => {
        utils.alert.show({
            title: 'Payment Details',
            message: `To provide the best experience possible, we've moved payment history and management over to AFT. You can view details for this payment using the Applied Fire Technologies website.`
        });
    }

    const onRemovePaymentMethod = method => {
        utils.alert.show({
            title: 'Remove Payment Method',
            message: `Are you sure that you wnat to remove this payment method? This can not be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Remove',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'deafult'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onRemovePaymentMethodconfirm(method);
                    return;
                }
            }
        })
    }

    const onRemovePaymentMethodconfirm = async method => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            await Request.post(utils, '/payments/', {
                card_id: method.id,
                source_category: method.source_category && method.source_category.code,
                type: 'remove_payment_method',
            });

            setLoading(false);
            setMethods(methods => {
                return methods.filter(prev_method => {
                    return prev_method.id !== method.id;
                })
            });

            // notify subscribers that payment methods have changed
            utils.events.emit('payment_method_change');

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue removing this payment method from your account. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onSelectPaymentMethod = method => {
        utils.sheet.show({
            items: [{
                key: 'remove',
                title: 'Remove',
                style: 'destructive'
            }]
        }, key => {
            if(key === 'remove') {
                onRemovePaymentMethod(method);
                return;
            }
        })
    }

    const getStripeCardComponent = () => {
        return (
            <Elements stripe={utils.stripe}>
                <ElementsConsumer>
                    {props => {
                        stripeProps.current = props;
                        return (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                width: '100%',
                                padding: 10
                            }}>
                                <div style={{
                                    flexGrow: 1
                                }}>
                                    <CardElement
                                    onChange={({ complete }) => setNewMethodCompleted(complete)}
                                    options={{
                                        hidePostalCode: true,
                                        style: {
                                            invalid: {
                                                color: Appearance.colors.red,
                                            },
                                            base: {
                                                fontSize: '12px',
                                                color: Appearance.colors.text(),
                                                '::placeholder': {
                                                    color: Appearance.colors.subText(),
                                                },
                                            }
                                        }
                                    }} />
                                </div>
                                {newMethodCompleted && (
                                    <img
                                    className={'text-button'}
                                    onClick={onAddNewMethod}
                                    src={'images/next-arrow-green-small.png'}
                                    style={{
                                        width: 20,
                                        height: 20,
                                        objectFit: 'contain',
                                        marginLeft: 8
                                    }} />
                                )}
                            </div>
                        )
                    }}
                </ElementsConsumer>
            </Elements>
        )
    }

    const getPayments = category => {

        let targets = category === Payment.Method.source_categories.get().user ? userPayments : clientPayments;
        if(targets === null) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 15
                }}>
                    <LottieView
                    loop={true}
                    autoPlay={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }
        
        let filtered_payments = targets.filter(payment => payment.source_category.code === category);
        if(filtered_payments.length === 0) {
            return (
                Views.entry({
                    title: 'Nothing to see here',
                    subTitle: 'No payments have been made with your account',
                    hideIcon: true,
                    bottomBorder: false
                })
            )
        }
        return (
            <>
            {filtered_payments.map((payment, index, payments) => {
                return (
                    Views.entry({
                        badge: [{
                            text: Utils.toCurrency(payment.amount),
                            color: payment.status.color
                        }],
                        bottomBorder: index !== payments.length - 1,
                        hideIcon: true,
                        key: index,
                        subTitle: `From ${Utils.formatDate(payment.date)}`,
                        title: payment.category.text,
                        onClick: onPaymentClick.bind(this, payment)
                    })
                )
            })}
            {category === Payment.Method.source_categories.get().user && (
                <PageControl
                data={userPaymentsPaging}
                limit={userPaymentsLimit}
                offset={userPaymentsOffset}
                onClick={next => setUserPaymentsOffset(next)}/>
            )}
            {category === Payment.Method.source_categories.get().client && (
                <PageControl
                data={clientPaymentsPaging}
                limit={clientPaymentsLimit}
                offset={clientPaymentsOffset}
                onClick={next => setClientPaymentsOffset(next)}/>
            )}
            </>
        )
    }

    const getPaymentComponents = () => {

        let isPrimary = utils.user.get().level <= User.levels.get().primary;
        return (
            <>
            <LayerItem
            collapsed={false}
            lastItem={isPrimary ? false : true}
            title={utils.user.get().level > User.levels.get().primary ? 'Payments' : 'Personal Payments'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel()
                }}>
                    {getPayments(Payment.Method.source_categories.get().user)}
                </div>
            </LayerItem>
            {isPrimary && (
                <LayerItem
                collapsed={false}
                lastItem={true}
                title={'Client Payments'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel()
                    }}>
                        {getPayments(Payment.Method.source_categories.get().client)}
                    </div>
                </LayerItem>
            )}
            </>
        )
    }

    const getPaymentMethods = () => {
        if(methods === null) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 15
                }}>
                    <LottieView
                    loop={true}
                    autoPlay={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }
        if(methods.length === 0) {
            return (
                Views.entry({
                    title: 'Nothing to see here',
                    subTitle: 'No payment methods have been added to your account',
                    hideIcon: true,
                    bottomBorder: false
                })
            )
        }

        return (
            <>
            {methods.map((method, index) => {
                return (
                    <div
                    key={index}
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: '100%',
                        padding: 10,
                        borderBottom: `1px solid ${Appearance.colors.divider()}`
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingRight: 8
                        }}>
                            <i
                            className={getIcon('payment')}
                            style={{
                                color: Appearance.colors.text(),
                                fontSize: 13
                            }}/>
                        </div>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            color: Appearance.colors.text(),
                            flexGrow: 1
                        }}>{method.getTitle()}</span>
                        <img
                        className={'text-button'}
                        onClick={onRemovePaymentMethod.bind(this, method)}
                        src={'images/red-x-icon.png'}
                        style={{
                            width: 20,
                            height: 20,
                            objectFit: 'contain',
                            marginLeft: 8
                        }} />
                    </div>
                )
            })}
            {getStripeCardComponent()}
            </>
        )
    }

    const fetchClientPayments = async () => {
        try {
            let { paging, payments } = await Request.get(utils, '/payments/', {
                client_id: utils.client.get().id,
                limit: clientPaymentsLimit,
                offset: clientPaymentsOffset,
                source_category: Payment.Method.source_categories.get().client,
                type: 'all'
            });
            setClientPaymentsPaging(paging);
            setClientPayments(payments.map(payment => Payment.create(payment)));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the client payment history. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const fetchUserPayments = async () => {
        try {
            let { paging, payments } = await Request.get(utils, '/payments/', {
                limit: userPaymentsLimit,
                offset: userPaymentsOffset,
                source_category: Payment.Method.source_categories.get().user,
                type: 'all',
                user_id: utils.user.get().user_id
            });
            setUserPaymentsPaging(paging);
            setUserPayments(payments.map(payment => Payment.create(payment)));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving your payment history. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchPaymentMethods = async () => {
        try {
            let { methods } = await Request.get(utils, '/payments/', {
                source_category: Payment.Method.source_categories.get().user,
                type: 'methods'
            });
            setMethods(methods.map(method => Payment.Method.create(method)));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving your credit options. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchClientPayments();
    }, [clientPaymentsOffset]);

    useEffect(() => {
        fetchUserPayments();
    }, [userPaymentsOffset]);

    useEffect(() => {
        fetchPaymentMethods();
        utils.events.on(layerID, 'payment_method_change', fetchPaymentMethods);
        return () => {
            utils.events.off(layerID, 'payment_method_change', fetchPaymentMethods);
        }
    }, []);

    return (
        <Layer
        id={layerID}
        index={index}
        title={`Payments for ${abstract.object.full_name}`}
        utils={utils}
        options={{
            ...options,
            loading: loading === true,
            layerState: layerState,
            sizing: 'medium'
        }}>
            <CreditsPicker
            utils={utils}
            onMethodAdded={onMethodAdded}
            onMethodRemoved={onMethodRemoved}
            onCreditsPurchased={onCreditsPurchased}/>

            <LayerItem
            title={'My Credit and Debit Cards'}
            collapsed={false}>
                <div style={{
                    ...Appearance.styles.unstyledPanel()
                }}>
                    {getPaymentMethods()}
                </div>
            </LayerItem>

            {getPaymentComponents()}
        </Layer>
    )
}

export const SystemEventDetails = ({ abstract, index, options, utils }) => {

    const layerID = `system_event_details_${abstract.getID()}`;
    const [target, setTarget] = useState(null);

    const onOutboundCallClick = async () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: target,
                type: 'outbound_calls'
            }),
            Component: OutboundCallDetails,
            id: `outbound_calls_details_${abstract.object.target.object.id}`,
            permissions: ['calls.details']
        });
    }

    const onCreatorClick = async () => {
        try {
            let user = await User.get(utils, abstract.object.user.user_id);
            utils.layer.open({
                id: `user_details_${user.user_id}`,
                abstract: Abstract.create({
                    type: 'users',
                    object: user
                }),
                Component: UserDetails
            });
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this account. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onAppointmentClick = async () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: target,
                type: 'appointments'
            }),
            Component: AppointmentDetails,
            id: `appointment_details_${abstract.object.target.object.id}`,
            permissions: ['appointments.details']
        });
    }

    const onLeadClick = async () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: target,
                type: 'leads'
            }),
            Component: LeadDetails,
            id: `lead_details_${abstract.object.target.object.id}`,
            permissions: ['leads.details']
        });
    }

    const onUserClick = async () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: target,
                type: 'users'
            }),
            Component: UserDetails,
            id: `user_details_${abstract.object.target.object.user_id}`,
            permissions: ['users.details']
        });
    }

    const getContentDiff = () => {
        let { components } = getSystemEventProps(abstract.object);
        if(components?.length === 0) {
            components = (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {Views.entry({
                        bottomBorder: false,
                        hideIcon: true,
                        subTitle: 'No public facing details are available for this event',
                        title: 'Nothing to see here'
                    })}
                </div>
            )
        }
        return (
            <LayerItem
            key={index}
            lastItem={true}
            title={'Details'}>
                {components}
            </LayerItem>
        )
    }

    const getTarget = () => {

        let props = {};
        let title = Utils.ucFirst(abstract.object.target.type);

        // return loading placeholder if target is not prepared
        if(!target) {
            return (
                <LayerItem title={title}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel()
                    }}>
                        {Views.entry({
                            title: `Loading...`,
                            hideIcon: true,
                            bottomBorder: false,
                            bottomBorder: false,
                        })}
                    </div>
                </LayerItem>
            );
        }

        // wrap abstract target attemps in a try/catch for objects that are deleted from the database
        // crash will happend otherwise for undefined nested values
        try {
            switch(abstract.object.target.type) {

                case 'appointments':
                title = 'Appointment';
                props = {
                    title: target.lead.full_name,
                    subTitle: target.lead.phone_number,
                    hideIcon: true,
                    onClick: onAppointmentClick
                }
                break;

                case 'leads':
                title = 'Lead';
                props = {
                    title: target.full_name,
                    subTitle: target.phone_number,
                    hideIcon: true,
                    onClick: onLeadClick
                }
                break;

                case 'outbound_calls':
                title = target.method === 'email' ? 'Email' : 'Phone Call';
                props = {
                    title: target.author ? target.author.full_name : 'Name not available',
                    subTitle: moment(target.start_date).format('MMMM Do, YYYY [at] h:mma'),
                    hideIcon: true,
                    onClick: onOutboundCallClick
                }
                break;

                case 'users':
                title = User.levels.toText(target.level);
                props = {
                    title: target.full_name,
                    subTitle: target.phone_number,
                    hideIcon: true,
                    onClick: onUserClick
                }
                break;

                default:
                return null;
            }

        } catch(e) {
            return null;
        }

        return (
            <LayerItem title={title}>
                <div style={{
                    ...Appearance.styles.unstyledPanel()
                }}>
                    {Views.entry({
                        ...props,
                        bottomBorder: false,
                        bottomBorder: false,
                    })}
                </div>
            </LayerItem>
        );
    }

    const getUser = () => {
        let { user } = abstract.object;
        if(!user) {
            return null;
        }

        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                marginBottom: 20
            }}>
                {Views.entry({
                    badge: {
                        text: Utils.formatDate(abstract.object.date),
                        color: Appearance.colors.primary()
                    },
                    bottomBorder: false,
                    icon: {
                        path: user.avatar
                    },
                    onClick: onCreatorClick,
                    subTitle: user.email_address,
                    title: user.full_name
                })}
            </div>
        )
    }

    const fetchTarget = async () => {
        try {
            switch(abstract.object.target.type) {

                case 'appointments':
                let appointment = await Appointment.get(utils, abstract.object.target.object.id);
                setTarget(appointment);
                break;

                case 'leads':
                let lead = await Lead.get(utils, abstract.object.target.object.id);
                setTarget(lead);
                break;

                case 'outbound_calls':
                let call = await OutboundCall.get(utils, abstract.object.target.object.id);
                setTarget(call);
                break;

                case 'users':
                let user = await User.get(utils, abstract.object.target.object.user_id);
                setTarget(user);
                break;
            }

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving some of the information for this system event. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchTarget();
    }, []);

    return (
        <Layer
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            sizing: 'medium'
        }}>
            {getUser()}
            {getTarget()}
            {getContentDiff()}
        </Layer>
    )
}

export const UserDetails = ({ abstract, index, options, utils }) => {

    const layerID = `user_details_${abstract.getID()}`;
    const [client, setClient] = useState(abstract.object.client);
    const [loading, setLoading] = useState(false);
    const [permissions, setPermissions] = useState(abstract.object.permissions || {});
    const [user, setUser] = useState(abstract.object);

    const canEditUser = () => {
        try {

            // prevent someone from editing an account with higher access than them
            let currentUser = utils.user.get();
            if(user.level < currentUser.level) {
                throw new Error('You are not authorized to make changes to this type of account');
            }

            // only allow editing for owner of user record or administrators
            if(currentUser.user_id !== user.user_id) {

                // check if current user client matches target user client
                if(currentUser.level > User.levels.get().primary) {
                    throw new Error('You are not authorized to make changes to this type of account');
                }
                // check if current user client matches target user client
                if(currentUser.level !== User.levels.get().admin && currentUser.client_id !== user.client_id) {
                    throw new Error('You are not authorized to make changes to accounts in this Client. You can only make changes to accounts in your own Client');
                }
            }
            return true;

        } catch(e) {
            return e;
        }
    }

    const onEditClick = async () => {
        utils.layer.open({
            abstract: abstract,
            Component: AddEditUser.bind(this, { isNewTarget: false }),
            id: `edit_user_${abstract.getID()}`
        });
    }

    const onEditPermissionsClick = () => {

        // prevent permissions editing for the current user if development mode is disabled
        let user = utils.user.get();
        if(API.dev_env === false && user.user_id === abstract.getID()) {
            utils.alert.show({
                title: 'Just a Second',
                message: `You are not able to change the permissions for your own account. ${user.level > User.levels.get().primary ? 'Please speak with your primary if you have any questions.' : 'Please contact support if you have any questions.'}`
            });
            return;
        }

        // open permissions editing layer
        utils.layer.open({
            id: `user_permissions_${abstract.getID()}`,
            abstract: abstract,
            Component: UserPermissions.bind(this, {
                onChange: result => {
                    abstract.object.permissions = result;
                    setPermissions(result);
                }
            })
        });
    }

    const onInvalidateCaches = () => {
        utils.alert.show({
            title: 'Invalidate Caches',
            message: `Are you sure that you want to invalidate the caches for this user? This will force the system to refresh all cached data for this user account. This action can not be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onInvalidateCachesConfirm();
                    return;
                }
            }
        });
    }

    const onInvalidateCachesConfirm = async () => {
        try {

            setLoading('options');
            await Request.post(utils, '/users/', {
                type: 'invalidate_caches',
                user_id: abstract.getID()
            });

            setLoading(false);
            utils.banner.show({
                title: 'Caches Invalidated',
                message: `The caches for this user have been successfully invalidated. Changes should be reflected shortly.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue invalidating the caches for this user. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onLoginAsUser = async () => {
        try {
            setLoading('options');
            let { token } = await Request.get(utils, '/users/', {
                type: 'new_single_use_login_token',
                user_id: abstract.getID()
            });

            console.log(`${API.server}/?route=admin_login_as_user&token=${encodeURIComponent(token)}`);
            setLoading(false);
            window.open(`${API.server}/?route=admin_login_as_user&token=${encodeURIComponent(token)}`);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing the lgoin credentials for this user account. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onPermissionsChange = data => {
        setPermissions(data);
    }

    const onOptionsClick = evt => {

        let { admin } = User.levels.get();
        let { level, user_id } = utils.user.get();

        utils.sheet.show({
            items: [{
                key: 'invalidate_caches',
                title: 'Invalidate Caches',
                style: 'default',
                visible: level <= admin
            },{
                key: 'login',
                title: 'Login as User',
                style: 'default',
                visible: user.level > admin && level <= admin
            },{
                key: 'availability',
                title: 'Manage Availability',
                style: 'default',
                visible: user.user_id === user_id || level <= admin
            },{
                key: 'permissions',
                title: 'Set Permissions',
                style: 'default',
                visible: canEditUser() === true,
                visible: false
            },{
                key: 'states',
                title: 'View Appointment History',
                style: 'default',
                visible: false
            }],
            target: evt.target
        }, key => {
            if(key === 'availability') {
                utils.layer.open({
                    abstract: abstract,
                    Component: UserAvailabilityEditor,
                    id: `user_availability_${abstract.getID()}`
                });
                return;
            }
            if(key === 'invalidate_caches') {
                onInvalidateCaches();
                return;
            }
            if(key === 'login') {
                onLoginAsUser();
                return;
            }
            if(key === 'permissions') {
                //onEditPermissionsClick();
                return;
            }
            if(key === 'states') {
                //onEditPermissionsClick();
                //return;
            }
        });
    }

    const getButtons = () => {
        
        return [{
            color: 'secondary',
            key: 'options',
            loading: loading === 'options',
            onClick: onOptionsClick,
            text: 'Options',
            visible: utils.user.get().level <= User.levels.get().primary
        },{
            color: canEditUser() === true ? 'primary' : 'danger',
            key: 'edit',
            onClick: onEditClick,
            text: 'Edit'
        }];
    }

    const getFields = () => {

        if(!user) {
            return [];
        }

        let items = [{
            key: 'details',
            permissions: ['users.details.general'],
            title: 'Details',
            items: [{
                key: 'level',
                title: 'Account Type',
                value: User.levels.toText(user.level)
            },{
                key: 'client',
                title: 'Client',
                visible: utils.user.get().level <= User.levels.get().admin,
                value: client ? client.name : null
            },{
                key: 'first_name',
                title: 'First Name',
                value: user.first_name
            },{
                key: 'last_name',
                title: 'Last Name',
                value: user.last_name
            },{
                key: 'last_login',
                title: 'Last Login',
                value: user.last_login ? moment(user.last_login).format('MMMM Do, YYYY [at] h:mma') : 'Not available'
            },{
                color: user.active ?  Appearance.colors.green : Appearance.colors.red,
                key: 'active',
                title: 'Status',
                value: user.active ? 'Active' : 'Inactive'
            },{
                key: 'user_id',
                title: 'User ID',
                value: user.user_id
            },{
                key: 'username',
                title: 'Username',
                value: user.username
            }]
        },{
            key: 'contact',
            permissions: ['users.details.contact'],
            title: 'Contact Information',
            items: [{
                key: 'email',
                title: 'Email Address',
                value: user.email_address
            },{
                key: 'phone',
                title: 'Phone Number',
                value: user.phone_number ? Utils.formatPhoneNumber(user.phone_number) : null
            },{
                key: 'slack_member_id',
                title: 'Slack Member ID',
                value: user.preferences?.slack_member_id
            }]
        },{
            key: 'location',
            permissions: ['users.details.location'],
            title: 'Location',
            items: [{
                key: 'location',
                title: 'Location',
                component: 'map',
                visible:  user.address && user.location ? true : false,
                value: user.location
            },{
                key: 'address',
                title: 'Physical Address',
                value: user.address ? Utils.formatAddress(user.address) : null
            },{
                key: 'timezone',
                title: 'Timezone',
                value: user.timezone
            }]
        }];

        // prevent moving forward if no valid items were provided
        let count = formatFields(utils, items).length;
        if(count === 0) {
            return null;
        }

        return (
            <FieldMapper
            sections={items}
            utils={utils}/>
        );
    }

    const connectToSockets = async () => {
        try {
            await utils.sockets.on('users', `on_permissions_change_${abstract.getID()}`, onPermissionsChange);
        } catch(e) {
            console.error(e.message)
        }
    }

    const disconnectFromSockets = async () => {
        try {
            await utils.sockets.off('users', `on_permissions_change_${abstract.getID()}`, onPermissionsChange);
        } catch(e) {
            console.error(e.message)
        }
    }

    const setupTarget = async () => {
        try {

            // fetch details for client if no client has been set
            if(!abstract.object.client && abstract.object.client_id) {
                let client = await Client.get(utils, abstract.object.client_id);
                abstract.object.client = client;
                setClient(client);
            }

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing some of your account information. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {

        connectToSockets();
        setupTarget();
        utils.content.subscribe(layerID, ['appointments', 'appointment_status', 'users'], {
            onUpdate: next => {
                if(next.type === 'users' && next.getID() === abstract.getID()) {
                    abstract.object = next.object;
                    setUser(next.object);
                    return;
                }
                setupTarget();
            }
        });
        return () => {
            disconnectFromSockets();
            utils.content.unsubscribe(layerID);
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            loading: loading,
            sizing: 'medium'
        }}>
            {getFields()}
        </Layer>
    )
}

export const UserPermissions = ({ onChange }, { abstract, index, options, utils }) => {

    const layerID = `user_permissions_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [edits, setEdits] = useState(abstract.object.permissions && abstract.object.permissions.values || {});
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templates, setTemplates] = useState([]);
    console.log(abstract.object.permissions);

    const onRemoveTemplate = item => {
        utils.alert.show({
            title: 'Remove Template',
            message: `Are you sure that you want to remove the "${item.title}" template?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onRemoveTemplateConfirm({ id: item.id, name: item.title });
                    return; 
                }
            }
        });
    }

    const onRemoveTemplateConfirm = async target => {
        try {
            setLoading(true);
            await Request.post(utils, '/users/', {
                id: target.id,
                type: 'remove_user_permissions_template'
            });

            // update local state with new list of templates
            setLoading(false);
            setTemplates(templates => templates.filter(template => template.id !== target.id));

            // update local state for selected template if applicable
            setSelectedTemplate(template => template && template.id === target.id ? null : template);

            // show confirmation alert
            utils.banner.show({
                title: 'Template Removed',
                message: `The "${target.name}" template  has been removed`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue removing this template. ${e.message || 'An unknown error occurred'}`
            });
        }
    }
    
    const onRequestTemplateName = async () => {
        return new Promise((resolve, reject) => {
            let name = null;
            utils.alert.show({
                title: 'Save as Template',
                message: 'What would you like to call this template?',
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField 
                        autoCapitalize={'words'}
                        fieldStyle={{ fieldStyle: 'center' }}
                        onChange={text => name = text}
                        placeholder={'Template Name'} />
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Done',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(name && key === 'confirm') {
                        resolve({ name });
                        return;
                    }
                    let error = new Error('user-cancelled');
                    reject(error);
                }
            });
        });
    }

    const onSelectionChange = (section, value) => {
        setEdits(edits => {
            section.items.forEach(item => {
                edits[item.key] = value;
            });
            return { ...edits };
        });
    }

    const onSaveTemplate = async () => {
        try {

            // request name for template from user
            let { name } = await onRequestTemplateName();

            // set loading flag and submit request
            setLoading('template');
            let { template } = await Request.post(utils, '/users/', {
                name: name,
                permissions: edits,
                type: 'new_user_permissions_template',
                user_id: abstract.getID()
            });

            // add template to list of templates
            setTemplates(items => {
                return update(items, {
                    $push: [template]
                }).sort((a,b) => {
                    return a.name.localeCompare(b.name);
                });
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Template Saved',
                message: `The permissions for this account have been saved as a template named "${name}"`
            });
            
        } catch(e) {
            setLoading(false);
            if(e.message === 'user-cancelled') {
                return;
            }
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue saving your template. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSubmit = async () => {
        try {

            // set loading flag and submit request
            setLoading('submit');
            let { permissions } = await Request.post(utils, '/users/', {
                permissions: edits,
                type: 'update_permissions',
                user_id: abstract.getID()
            });

            // notify subscribers of data change
            if(typeof(onChange) === 'function') {
                onChange(permissions);
            }

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Permissions Updated',
                message: `The permissions for this account have been updated and will be applied immediately.`,
                onShow: setLayerState.bind(this, 'close')
            });
            
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the permissions for this account. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onTemplateChange = item => {

        // update local state with selected item
        setSelectedTemplate(item);

        // declare template object using selected item
        let target = templates.find(template => item && item.id === template.id);

        // prevent moving forward if no template was found
        if(!target) {
            return;
        }

        // apply template permissions to user permissions edits
        setEdits(edits => ({
            ...edits,
            ...target.permissions
        }));
    }

    const onVisibilityChange = item => {
        setEdits(edits => ({ 
            ...edits,
            [item.key]: !item.selected
        }));
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'template',
            loading: loading === 'template',
            onClick: onSaveTemplate,
            text: 'Save as Template'
        },{
            color: 'primary',
            key: 'submit',
            loading: loading === 'submit',
            onClick: onSubmit,
            text: 'Submit'
        }];
    }

    const getContent = () => {

        // prepare field components
        let fields = getFields().map(section => {
            section.collapsed = false;
            section.items = section.items.map(item => ({
                ...item,
                component: 'auto_toggle',
                rightContent: (
                    <img
                    src={edits[item.key] ? `images/checkmark-button-green-small.png` : `images/red-x-icon.png`}
                    style={{
                        height: 20,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 20
                    }} />
                ),
                selected: edits[item.key] ? true : false,
                value: (
                    <span style={{
                        ...Appearance.textStyles.value(),
                        color: edits[item.key] ? Appearance.colors.green : Appearance.colors.red
                    }}>{edits[item.key] ? 'Enabled' : 'Disabled'}</span>
                )
            }));
            return section;
        });

        return (
            <FieldMapper 
            editable={true}
            sections={fields}
            onEditClick={onVisibilityChange} 
            utils={utils} />
        )
    }

    const getFields = () => {

        return [{
            key: 'calendar',
            rightContent: getSelectionButtons,
            title: 'Calendar',
            items: [{
                component: 'bool_list',
                key: 'calendar.time_view',
                title: 'Time View',
                value: edits['calendar.time_view']
            },{
                component: 'bool_list',
                key: 'calendar.week_view',
                title: 'Week View',
                value: edits['calendar.week_view']
            }]
        },{
            key: 'events.actions',
            rightContent: getSelectionButtons,
            title: 'Calendar Events',
            items: [{
                component: 'bool_list',
                key: 'events.actions.new',
                title: 'Create',
                value: edits['events.actions.new']
            },{
                component: 'bool_list',
                key: 'events.actions.delete',
                title: 'Delete',
                value: edits['events.actions.delete']
            },{
                component: 'bool_list',
                key: 'events.actions.download',
                title: 'Download',
                value: edits['events.actions.download']
            },{
                component: 'bool_list',
                key: 'events.actions.edit',
                title: 'Edit',
                value: edits['events.actions.edit']
            }]
        },{
            key: 'events.notes',
            rightContent: getSelectionButtons,
            title: 'Calendar Event Notes',
            items: [{
                component: 'bool_list',
                key: 'events.notes.actions.new',
                title: 'Create',
                value: edits['events.notes.actions.new']
            },{
                component: 'bool_list',
                key: 'events.notes.actions.delete',
                title: 'Delete',
                value: edits['events.notes.actions.delete']
            },{
                component: 'bool_list',
                key: 'events.notes.actions.edit',
                title: 'Edit',
                value: edits['events.notes.actions.edit']
            },{
                component: 'bool_list',
                key: 'events.notes',
                title: 'View',
                value: edits['events.notes']
            }]
        },{
            key: 'calls',
            rightContent: getSelectionButtons,
            title: 'Outbound Calls',
            items: [{
                component: 'bool_list',
                key: 'calls.details.ext',
                title: 'Additional Details',
                value: edits['calls.details.ext']
            },{
                component: 'bool_list',
                key: 'calls.calendar',
                title: 'Calendar',
                value: edits['calls.calendar']
            },{
                component: 'bool_list',
                key: 'calls.details.dates',
                title: 'Dates and Times',
                value: edits['calls.details.dates']
            },{
                component: 'bool_list',
                key: 'calls.details.general',
                title: 'General Details',
                value: edits['calls.details.general']
            },{
                component: 'bool_list',
                key: 'calls.list.all',
                title: 'General List',
                value: edits['calls.list.all']
            },{
                component: 'bool_list',
                key: 'calls.details.system_events',
                title: 'System Events',
                value: edits['calls.details.system_events']
            }]
        },{
            key: 'appointments',
            rightContent: getSelectionButtons,
            title: 'Appointment Lists',
            items: [{
                component: 'bool_list',
                key: 'appointments.list.customer_responses',
                title: 'Customer Responses',
                value: edits['appointments.list.customer_responses']
            },{
                component: 'bool_list',
                key: 'appointments.list.all',
                title: 'View',
                value: edits['appointments.list.all']
            }]
        },{
            key: 'appointments',
            rightContent: getSelectionButtons,
            title: 'Appointment Actions',
            items: [{
                component: 'bool_list',
                key: 'appointments.actions.cancel',
                title: 'Cancel',
                value: edits['appointments.actions.cancel']
            },{
                component: 'bool_list',
                key: 'appointments.actions.status',
                title: 'Change Status',
                value: edits['appointments.actions.status']
            },{
                component: 'bool_list',
                key: 'appointments.actions.delete',
                title: 'Delete',
                value: edits['appointments.actions.delete']
            },{
                component: 'bool_list',
                key: 'appointments.actions.edit',
                title: 'Edit',
                value: edits['appointments.actions.edit']
            },{
                component: 'bool_list',
                key: 'appointments.actions.import',
                title: 'Import',
                value: edits['appointments.actions.import']
            },{
                component: 'bool_list',
                key: 'appointments.actions.print',
                title: 'Print',
                value: edits['appointments.actions.print']
            },{
                component: 'bool_list',
                key: 'appointments.actions.reschedule',
                title: 'Reschedule',
                value: edits['appointments.actions.reschedule']
            },{
                component: 'bool_list',
                key: 'appointments.actions.set',
                title: 'Set',
                value: edits['appointments.actions.set']
            }]
        },{
            key: 'appointments.details',
            rightContent: getSelectionButtons,
            title: 'Appointment Details',
            items: [{
                component: 'bool_list',
                key: 'appointments.details.users',
                title: 'Assignments and Credit',
                value: edits['appointments.details.users']
            },{
                component: 'bool_list',
                key: 'appointments.details.calls',
                title: 'Calls and Emails',
                value: edits['appointments.calls']
            },{
                component: 'bool_list',
                key: 'appointments.details.general',
                title: 'General Details',
                value: edits['appointments.details.general']
            },{
                component: 'bool_list',
                key: 'appointments.details.lead',
                title: 'Lead Details',
                value: edits['appointments.details.lead']
            },{
                component: 'bool_list',
                key: 'appointments.details.system_events',
                title: 'System Events',
                value: edits['appointments.details.system_events']
            },{
                component: 'bool_list',
                key: 'appointments.details',
                title: 'View',
                value: edits['appointments.details']
            }]
        },{
            key: 'appointments.notes',
            rightContent: getSelectionButtons,
            title: 'Appointment Notes',
            items: [{
                component: 'bool_list',
                key: 'appointments.notes.actions.new',
                title: 'Create',
                value: edits['appointments.notes.actions.new']
            },{
                component: 'bool_list',
                key: 'appointments.notes.actions.delete',
                title: 'Delete',
                value: edits['appointments.notes.actions.delete']
            },{
                component: 'bool_list',
                key: 'appointments.notes.actions.edit',
                title: 'Edit',
                value: edits['appointments.notes.actions.edit']
            },{
                component: 'bool_list',
                key: 'appointments.notes',
                title: 'View',
                value: edits['appointments.notes']
            }]
        },{
            key: 'connect',
            rightContent: getSelectionButtons,
            title: 'Connect',
            items: [{
                component: 'bool_list',
                key: 'connect',
                title: 'View',
                value: edits['connect']
            }]
        },{
            key: 'leads',
            rightContent: getSelectionButtons,
            title: 'Lead Lists',
            items: [{
                component: 'bool_list',
                key: 'leads.list.general',
                title: 'All',
                value: edits['leads.list.general']
            },{
                component: 'bool_list',
                key: 'leads.list.archived',
                title: 'Archived',
                value: edits['leads.list.archived']
            },{
                component: 'bool_list',
                key: 'leads.list.assigned',
                title: 'Assigned',
                value: edits['leads.list.assigned']
            },{
                component: 'bool_list',
                key: 'leads.list.do_not_call',
                title: 'Do Not Call',
                value: edits['leads.list.do_not_call']
            },{
                component: 'bool_list',
                key: 'leads.list.follow_up',
                title: 'Follow Ups',
                value: edits['leads.list.follow_up']
            },{
                component: 'bool_list',
                key: 'leads.list.locations',
                title: 'Locations',
                value: edits['leads.list.locations']
            },{
                component: 'bool_list',
                key: 'leads.list.new',
                title: 'New',
                value: edits['leads.list.new']
            },{
                component: 'bool_list',
                key: 'leads.list.out_of_service_area',
                title: 'Out of Service Area',
                value: edits['leads.list.out_of_service_area']
            },{
                component: 'bool_list',
                key: 'leads.list.scripts',
                title: 'Scripts',
                value: edits['leads.list.scripts']
            },{
                component: 'bool_list',
                key: 'leads.list.sub_types',
                title: 'Sub-Types',
                value: edits['leads.list.sub_types']
            },{
                component: 'bool_list',
                key: 'leads.list.transfers',
                title: 'Transfers',
                value: edits['leads.list.transfers']
            },{
                component: 'bool_list',
                key: 'leads.list.types',
                title: 'Types',
                value: edits['leads.list.types']
            },{
                component: 'bool_list',
                key: 'leads.list.unreleased',
                title: 'Unreleased',
                value: edits['leads.list.unreleased']
            }]
        },{
            key: 'leads',
            rightContent: getSelectionButtons,
            title: 'Lead Actions',
            items: [{
                component: 'bool_list',
                key: 'leads.actions.do_not_call.new',
                title: 'Add to Do Not Call List',
                value: edits['leads.actions.do_not_call.new']
            },{
                component: 'bool_list',
                key: 'leads.actions.archive',
                title: 'Archive',
                value: edits['leads.actions.archive']
            },{
                component: 'bool_list',
                key: 'leads.actions.status',
                title: 'Change Status',
                value: edits['leads.actions.status']
            },{
                component: 'bool_list',
                key: 'leads.actions.new',
                title: 'Create',
                value: edits['leads.actions.new']
            },{
                component: 'bool_list',
                key: 'leads.actions.delete',
                title: 'Delete',
                value: edits['leads.actions.delete']
            },{
                component: 'bool_list',
                key: 'leads.actions.disqualify',
                title: 'Disqualify',
                value: edits['leads.actions.disqualify']
            },{
                component: 'bool_list',
                key: 'leads.actions.download',
                title: 'Download',
                value: edits['leads.actions.download']
            },{
                component: 'bool_list',
                key: 'leads.actions.edit',
                title: 'Edit',
                value: edits['leads.actions.edit']
            },{
                component: 'bool_list',
                key: 'leads.actions.import',
                title: 'Import',
                value: edits['leads.actions.import']
            },{
                component: 'bool_list',
                key: 'leads.actions.print',
                title: 'Print',
                value: edits['leads.actions.print']
            },{
                component: 'bool_list',
                key: 'leads.actions.release',
                title: 'Release',
                value: edits['leads.actions.release']
            },{
                component: 'bool_list',
                key: 'leads.actions.do_not_call.delete',
                title: 'Remove from Do Not Call List',
                value: edits['leads.actions.do_not_call.delete']
            },{
                component: 'bool_list',
                key: 'leads.actions.sms',
                title: 'Send Text Message',
                value: edits['leads.actions.sms']
            },{
                component: 'bool_list',
                key: 'leads.actions.set_appointment',
                title: 'Set Appointment',
                value: edits['leads.actions.set_appointment']
            },{
                component: 'bool_list',
                key: 'leads.actions.transfer',
                title: 'Transfer',
                value: edits['leads.actions.transfer']
            },{
                component: 'bool_list',
                key: 'leads.actions.unassign',
                title: 'Unassign',
                value: edits['leads.actions.unassign']
            },{
                component: 'bool_list',
                key: 'leads.actions.unrelease',
                title: 'Unrelease',
                value: edits['leads.actions.unrelease']
            }]
        },{
            key: 'leads.board',
            rightContent: getSelectionButtons,
            title: 'Lead Board',
            items: [{
                component: 'bool_list',
                key: 'leads.board.actions.print',
                title: 'Print',
                value: edits['leads.board.actions.print']
            },{
                component: 'bool_list',
                key: 'leads.board',
                title: 'View',
                value: edits['leads.board']
            }]
        },{
            key: 'lead.details',
            rightContent: getSelectionButtons,
            title: 'Lead Details',
            items: [{
                component: 'bool_list',
                key: 'leads.details.ext',
                title: 'Additional Details',
                value: edits['leads.details.ext']
            },{
                component: 'bool_list',
                key: 'leads.details.users',
                title: 'Assignments and Credit',
                value: edits['leads.details.users']
            },{
                component: 'bool_list',
                key: 'leads.details.unstructured',
                title: 'Custom Client Details',
                value: edits['leads.details.unstructured']
            },{
                component: 'bool_list',
                key: 'leads.details.customer',
                title: 'Customer Details',
                value: edits['leads.details.customer']
            },{
                component: 'bool_list',
                key: 'leads.details.customer_response',
                title: 'Customer Survey Response',
                value: edits['leads.details.customer_response']
            },{
                component: 'bool_list',
                key: 'leads.details.general',
                title: 'General Details',
                value: edits['leads.details.general']
            },{
                component: 'bool_list',
                key: 'leads.details.spouse',
                title: 'Spouse Details',
                value: edits['leads.details.spouse']
            },{
                component: 'bool_list',
                key: 'leads.details.system_events',
                title: 'System Events',
                value: edits['leads.details.system_events']
            },{
                component: 'bool_list',
                key: 'leads.details.sms',
                title: 'Text Messages',
                value: edits['leads.details.sms']
            },{
                component: 'bool_list',
                key: 'leads.details',
                title: 'View',
                value: edits['leads.details']
            }]
        },{
            key: 'lead.duplicates',
            rightContent: getSelectionButtons,
            title: 'Lead Duplicates',
            items: [{
                component: 'bool_list',
                key: 'leads.duplicates.actions.edit',
                title: 'Edit',
                value: edits['lead.duplicates.actions.edit']
            },{
                component: 'bool_list',
                key: 'leads.duplicates',
                title: 'View',
                value: edits['lead.duplicates']
            }]
        },{
            key: 'lead.notes',
            rightContent: getSelectionButtons,
            title: 'Lead Notes',
            items: [{
                component: 'bool_list',
                key: 'leads.notes.actions.new',
                title: 'Create',
                value: edits['lead.notes.actions.new']
            },{
                component: 'bool_list',
                key: 'leads.notes.actions.delete',
                title: 'Delete',
                value: edits['leads.notes.actions.delete']
            },{
                component: 'bool_list',
                key: 'leads.notes.actions.edit',
                title: 'Edit',
                value: edits['lead.notes.actions.edit']
            },{
                component: 'bool_list',
                key: 'leads.notes',
                title: 'View',
                value: edits['lead.notes']
            }]
        },{
            key: 'lead.scripts',
            rightContent: getSelectionButtons,
            title: 'Lead Scripts',
            items: [{
                component: 'bool_list',
                key: 'leads.scripts.actions.status',
                title: 'Change Status',
                value: edits['leads.scripts.actions.status']
            },{
                component: 'bool_list',
                key: 'leads.scripts.actions.new',
                title: 'Create',
                value: edits['leads.scripts.actions.new']
            },{
                component: 'bool_list',
                key: 'leads.scripts.actions.delete',
                title: 'Delete',
                value: edits['leads.scripts.actions.delete']
            },{
                component: 'bool_list',
                key: 'leads.scripts.actions.edit',
                title: 'Edit',
                value: edits['leads.scripts.actions.edit']
            },{
                component: 'bool_list',
                key: 'leads.scripts.details',
                title: 'General Details',
                value: edits['leads.scripts.details']
            },{
                component: 'bool_list',
                key: 'leads.scripts.actions.default',
                title: 'Set as Client Default',
                value: edits['leads.scripts.actions.default']
            }]
        },{
            key: 'lead.sub_types',
            rightContent: getSelectionButtons,
            title: 'Lead Sub-Types',
            items: [{
                component: 'bool_list',
                key: 'leads.sub_types.actions.status',
                title: 'Change Status',
                value: edits['leads.sub_types.actions.status']
            },{
                component: 'bool_list',
                key: 'leads.sub_types.actions.new',
                title: 'Create',
                value: edits['leads.sub_types.actions.new']
            },{
                component: 'bool_list',
                key: 'leads.sub_types.actions.delete',
                title: 'Delete',
                value: edits['leads.sub_types.actions.delete']
            },{
                component: 'bool_list',
                key: 'leads.sub_types.actions.edit',
                title: 'Edit',
                value: edits['leads.sub_types.actions.edit']
            },{
                component: 'bool_list',
                key: 'leads.sub_types.details',
                title: 'General Details',
                value: edits['leads.sub_types.details']
            }]
        },{
            key: 'lead.types',
            rightContent: getSelectionButtons,
            title: 'Lead Types',
            items: [{
                component: 'bool_list',
                key: 'leads.types.actions.status',
                title: 'Change Status',
                value: edits['leads.types.actions.status']
            },{
                component: 'bool_list',
                key: 'leads.types.actions.new',
                title: 'Create',
                value: edits['leads.types.actions.new']
            },{
                component: 'bool_list',
                key: 'leads.types.actions.delete',
                title: 'Delete',
                value: edits['leads.types.actions.delete']
            },{
                component: 'bool_list',
                key: 'leads.types.actions.edit',
                title: 'Edit',
                value: edits['leads.types.actions.edit']
            },{
                component: 'bool_list',
                key: 'leads.types.details',
                title: 'General Details',
                value: edits['leads.types.details']
            },{
                component: 'bool_list',
                key: 'leads.types.actions.default',
                title: 'Set as Client Default',
                value: edits['leads.types.actions.default']
            }]
        },{
            key: 'surveys.list',
            rightContent: getSelectionButtons,
            title: 'Survey and Survey Lists',
            items: [{
                component: 'bool_list',
                key: 'surveys.list.all',
                title: 'Surveys',
                value: edits['surveys.list.all']
            },{
                component: 'bool_list',
                key: 'surveys.list.enrollment',
                title: 'Sales Operative Enrollment',
                value: edits['surveys.list.enrollment']
            },{
                component: 'bool_list',
                key: 'surveys.list.surveys',
                title: 'Surveys',
                value: edits['surveys.list.surveys']
            },{
                component: 'bool_list',
                key: 'surveys.list.templates',
                title: 'Templates',
                value: edits['surveys.list.templates']
            }]
        },{
            key: 'surveys.actions',
            rightContent: getSelectionButtons,
            title: 'Survey Actions',
            items: [{
                component: 'bool_list',
                key: 'surveys.actions.status',
                title: 'Change Status',
                value: edits['surveys.actions.status']
            },{
                component: 'bool_list',
                key: 'surveys.actions.enroll',
                title: 'Create Enrollment',
                value: edits['surveys.actions.enroll']
            },{
                component: 'bool_list',
                key: 'surveys.actions.edit',
                title: 'Edit',
                value: edits['surveys.actions.edit']
            },{
                component: 'bool_list',
                key: 'surveys.actions.edit_agreement',
                title: 'Edit Agreement',
                value: edits['surveys.actions.edit_agreement']
            }]
        },{
            key: 'surveys.details',
            rightContent: getSelectionButtons,
            title: 'Survey Details',
            items: [{
                component: 'bool_list',
                key: 'surveys.details.ext',
                title: 'Additional Details',
                value: edits['surveys.details.ext']
            },{
                component: 'bool_list',
                key: 'surveys.details.general',
                title: 'General Details',
                value: edits['surveys.details.general']
            },{
                component: 'bool_list',
                key: 'surveys.details',
                title: 'View',
                value: edits['surveys.details']
            }]
        },{
            key: 'surveys.survey.actions',
            rightContent: getSelectionButtons,
            title: 'Survey Actions',
            items: [{
                component: 'bool_list',
                key: 'surveys.surveys.actions.url.status',
                title: 'Change Custom Url Status',
                value: edits['surveys.surveys.actions.url.status']
            },{
                component: 'bool_list',
                key: 'surveys.surveys.actions.status',
                title: 'Change Status',
                value: edits['surveys.surveys.actions.status']
            },{
                component: 'bool_list',
                key: 'surveys.surveys.actions.new',
                title: 'Create',
                value: edits['surveys.surveys.actions.new']
            },{
                component: 'bool_list',
                key: 'surveys.surveys.actions.url.new',
                title: 'Create Custom Url',
                value: edits['surveys.surveys.actions.url.new']
            },{
                component: 'bool_list',
                key: 'surveys.surveys.actions.edit',
                title: 'Edit',
                value: edits['surveys.surveys.actions.edit']
            }]
        },{
            key: 'surveys.survey.details',
            rightContent: getSelectionButtons,
            title: 'Survey Details',
            items: [{
                component: 'bool_list',
                key: 'surveys.surveys.details.ext',
                title: 'Additional Details',
                value: edits['surveys.surveys.details.ext']
            },{
                component: 'bool_list',
                key: 'surveys.surveys.details.urls',
                title: 'Custom Urls',
                value: edits['surveys.surveys.details.urls']
            },{
                component: 'bool_list',
                key: 'surveys.surveys.details.general',
                title: 'General Details',
                value: edits['surveys.surveys.details.general']
            },{
                component: 'bool_list',
                key: 'surveys.surveys.details.template',
                title: 'Template',
                value: edits['surveys.surveys.details.template']
            },{
                component: 'bool_list',
                key: 'surveys.surveys.details',
                title: 'View',
                value: edits['surveys.surveys.details']
            }]
        },{
            key: 'surveys.survey_monkey',
            rightContent: getSelectionButtons,
            title: 'Survey Monkey',
            items: [{
                component: 'bool_list',
                key: 'surveys.survey_monkey.actions.edit',
                title: 'Edit',
                value: edits['surveys.survey_monkey.actions.edit']
            },{
                component: 'bool_list',
                key: 'surveys.survey_monkey',
                title: 'View',
                value: edits['surveys.survey_monkey']
            }]
        },{
            key: 'surveys.templates.actions',
            rightContent: getSelectionButtons,
            title: 'Survey Template Actions',
            items: [{
                component: 'bool_list',
                key: 'surveys.templates.actions.status',
                title: 'Change Status',
                value: edits['surveys.templates.actions.status']
            },{
                component: 'bool_list',
                key: 'surveys.templates.actions.new',
                title: 'Create',
                value: edits['surveys.templates.actions.new']
            },{
                component: 'bool_list',
                key: 'surveys.templates.actions.edit',
                title: 'Edit',
                value: edits['surveys.templates.actions.edit']
            },{
                component: 'bool_list',
                key: 'surveys.templates.details',
                title: 'View',
                value: edits['surveys.templates.details']
            },]
        },{
            key: 'reports',
            rightContent: getSelectionButtons,
            title: 'Reports',
            items: [{
                component: 'bool_list',
                key: 'reports.appointments.appointment_overview',
                title: 'Appointment Appointment Overview',
                value: edits['reports.appointments.appointment_overview']
            },{
                component: 'bool_list',
                key: 'reports.users.just_the_facts',
                title: 'Just the Facts',
                value: edits['reports.users.just_the_facts']
            },{
                component: 'bool_list',
                key: 'reports.leads.clients',
                title: 'Lead Clients',
                value: edits['reports.leads.clients']
            },{
                component: 'bool_list',
                key: 'reports.leads.credit',
                title: 'Lead Credit',
                value: edits['reports.leads.credit']
            },{
                component: 'bool_list',
                key: 'reports.leads.follow_ups',
                title: 'Lead Follow Ups',
                value: edits['reports.leads.follow_ups']
            },{
                component: 'bool_list',
                key: 'reports.marketing.call_times',
                title: 'Marketing Call Times',
                value: edits['reports.marketing.call_times']
            },{
                component: 'bool_list',
                key: 'reports.users.setter_progress_overview',
                title: 'Setter Progress Overview',
                value: edits['reports.users.setter_progress_overview']
            },{
                component: 'bool_list',
                key: 'reports.marketing.statistics',
                title: 'Marketing Statistics',
                value: edits['reports.marketing.statistics']
            },{
                component: 'bool_list',
                key: 'reports.leads.pending',
                title: 'Pending Leads',
                value: edits['reports.leads.pending']
            },{
                component: 'bool_list',
                key: 'reports.surveys.registrations',
                title: 'Survey Registrations',
                value: edits['reports.surveys.registrations']
            },{
                component: 'bool_list',
                key: 'reports.surveys.associate_registrations',
                title: 'Sales Operative Survey Registrations',
                value: edits['reports.surveys.associate_registrations']
            },{
                component: 'bool_list',
                key: 'reports.users.sales_ops_progress_overview',
                title: 'Sales Operative Progress Overview',
                value: edits['reports.users.sales_ops_progress_overview']
            },{
                component: 'bool_list',
                key: 'reports.surveys.program_interactions',
                title: 'Survey Interactions',
                value: edits['reports.surveys.program_interactions']
            }]
            
        },{
            key: 'settings',
            rightContent: getSelectionButtons,
            title: 'Settings',
            items: [{
                component: 'bool_list',
                key: 'settings.actions.edit',
                title: 'Edit',
                value: edits['settings.actions.edit']
            },{
                component: 'bool_list',
                key: 'settings',
                title: 'View',
                value: edits['settings']
            }]
        },{
            key: 'users',
            rightContent: getSelectionButtons,
            title: 'User Lists',
            items: [{
                component: 'bool_list',
                key: 'users.list.availability',
                title: 'Availability',
                value: edits['users.list.availability']
            },{
                component: 'bool_list',
                key: 'users.list.booking_coordinators',
                title: 'Booking Coordinators',
                value: edits['users.list.booking_coordinators']
            },{
                component: 'bool_list',
                key: 'users.list.groups',
                title: 'Groups',
                value: edits['users.list.groups']
            },{
                component: 'bool_list',
                key: 'users.list.setters',
                title: 'Setters',
                value: edits['users.list.setters']
            },{
                component: 'bool_list',
                key: 'users.list.notifications',
                title: 'Notifications',
                value: edits['users.list.notifications']
            },{
                component: 'bool_list',
                key: 'users.list.sales_opss',
                title: 'Sales Operatives',
                value: edits['users.list.sales_opss']
            },{
                component: 'bool_list',
                key: 'users.list.system_events',
                title: 'System Events and Activities',
                value: edits['users.list.system_events']
            }]
        },{
            key: 'users.details',
            rightContent: getSelectionButtons,
            title: 'User Details',
            items: [{
                component: 'bool_list',
                key: 'users.details.ext',
                title: 'Additional Details',
                value: edits['users.details.ext']
            },{
                component: 'bool_list',
                key: 'users.details.contact',
                title: 'Contact Information',
                value: edits['users.details.contact']
            },{
                component: 'bool_list',
                key: 'users.details.general',
                title: 'General Details',
                value: edits['users.details.general']
            },{
                component: 'bool_list',
                key: 'users.details.location',
                title: 'Location',
                value: edits['users.details.location']
            },{
                component: 'bool_list',
                key: 'users.details.pay_rates',
                title: 'Pay Rates',
                value: edits['users.details.pay_rates']
            },{
                component: 'bool_list',
                key: 'users.details.permissions',
                title: 'Permissions',
                value: edits['users.details.permissions']
            },{
                component: 'bool_list',
                key: 'users.details',
                title: 'View',
                value: edits['users.details']
            }]
        },{
            key: 'users.groups.actions',
            rightContent: getSelectionButtons,
            title: 'User Group Actions',
            items: [{
                component: 'bool_list',
                key: 'users.groups.actions.status',
                title: 'Change Status',
                value: edits['users.groups.actions.status']
            },{
                component: 'bool_list',
                key: 'users.groups.actions.new',
                title: 'Create',
                value: edits['users.groups.actions.new']
            },{
                component: 'bool_list',
                key: 'users.groups.actions.edit',
                title: 'Edit',
                value: edits['users.groups.actions.edit']
            }]
        },{
            key: 'users.groups.details',
            rightContent: getSelectionButtons,
            title: 'User Group Details',
            items: [{
                component: 'bool_list',
                key: 'users.groups.details.permissions.appointments',
                title: 'Appointment Permissions',
                value: edits['users.groups.details.permissions.appointments']
            },{
                component: 'bool_list',
                key: 'users.groups.details.permissions.leads',
                title: 'Lead Permissions',
                value: edits['users.groups.details.permissions.leads']
            },{
                component: 'bool_list',
                key: 'users.groups.details.permissions.users',
                title: 'User Permissions',
                value: edits['users.groups.details.permissions.users']
            },{
                component: 'bool_list',
                key: 'users.groups.details.general',
                title: 'General Details',
                value: edits['users.groups.details.general']
            },{
                component: 'bool_list',
                key: 'users.groups.details',
                title: 'View',
                value: edits['users.groups.details']
            }]
        }];
    }

    const getSelectionButtons = section => {

        // prepare count for enabled and disabled items
        let disabled = section.items.filter(item => edits[item.key] === false);
        let enabled = section.items.filter(item => edits[item.key] === true);

        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
                justifyContent: 'flex-end',
                marginRight: 8
            }}>
                {disabled.length !== section.items.length && (
                    <AltBadge
                    onClick={onSelectionChange.bind(this, section, false)}
                    content={{
                        color: Appearance.colors.red,
                        text: 'Disable All',
                    }}
                    style={{
                        marginLeft: 4,
                        marginRight: 0,
                        padding: '2px 10px 2px 10px'
                    }}/>
                )}
                {enabled.length !== section.items.length && (
                    <AltBadge
                    onClick={onSelectionChange.bind(this, section, true)}
                    content={{
                        color: Appearance.colors.green,
                        text: 'Enable All'
                    }}
                    style={{
                        marginLeft: 4,
                        marginRight: 0,
                        padding: '2px 10px 2px 10px',
                    }}/>
                )}
            </div>
        )
    }

    const getTemplates = () => {
        return templates.length > 0 && (
            <LayerItem 
            childrenStyle={{
                overflow: 'visible'
            }}
            title={'Templates'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    overflow: 'visible',
                    padding: 12
                }}>
                    <CustomListField
                    items={getTemplateItems()}
                    onChange={onTemplateChange} 
                    onRemoveItem={onRemoveTemplate}
                    placeholder={'Select a template from the list...'}
                    removable={true} 
                    value={selectedTemplate} />
                </div>
            </LayerItem>
        )
    }

    const getTemplateItems = () => {
        return templates.map(template => ({
            id: template.id,
            removable: true,
            title: template.name
        }));
    }

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            let { templates } = await Request.get(utils, '/users/', {
                type: 'user_permission_templates'
            });

            setLoading(false);
            setTemplates(templates);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the list of permission templates. ${e.message || 'An unknown error occurred'}`
            });
        }
    }
    
    useEffect(() => {
        fetchTemplates();
    }, []);

    return (
        <Layer 
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={`Permissions for ${abstract.getTitle()}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            {getTemplates()}
            {getContent()}
        </Layer>
    )
}

// Components
export const getNotificationIconColor = notification => {
    if(notification.category.includes('CANCELLED')) {
        return Appearance.colors.red;
    }
    if(notification.category.includes('RESCHEDULED')) {
        return Appearance.colors.secondary();
    }
    return Appearance.colors.primary();
}

export const getSystemEventBadges = evt => {

    let badges = [];
    switch(evt.action.code) {
        case SystemEvent.actions.create:
        badges.push({
            text: 'Created',
            color: Appearance.colors.primary()
        });
        break;

        case SystemEvent.actions.update:
        badges.push({
            text: 'Updated',
            color: Appearance.colors.secondary()
        });
        break;

        case SystemEvent.actions.delete:
        badges.push({
            text: 'Deleted',
            color: Appearance.colors.red
        });
        break;

        case SystemEvent.actions.warning:
        badges.push({
            text: 'Warning',
            color: Appearance.colors.orange
        });
        break;

        case SystemEvent.actions.note:
        badges.push({
            text: 'Note',
            color: Appearance.colors.tertiary()
        });
        break;
    }
    return badges.concat([{
        text: Utils.formatDate(evt.date),
        color: Appearance.colors.grey()
    }])
}

export const getSystemEventProps = (evt, options = {}) => {

    // create => use abstract generated title information
    if(evt.action.code === SystemEvent.actions.create) {
        let val = `${evt.user.full_name} created a ${evt.target.getTitle().toLowerCase()}`;
        if(evt.target.type === 'user') {
            val = `${evt.user.full_name} created a ${User.levels.toText(evt.target.object.level)} account for ${evt.target.getTitle()}`;
        }
        return {
            values: [val],
            title: 'Created',
            components: (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: 8,
                    padding: '6px 12px 6px 12px'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        color: Appearance.colors.text(),
                        whiteSpace: 'normal'
                    }}>
                        <span style={{
                            fontWeight: 700,
                            color: Appearance.colors.text()
                        }}>
                            {evt.user.full_name}
                        </span>
                        {evt.target.type === 'user' ? ` created a ${User.levels.toText(evt.target.object.level)} account for ${evt.target.getTitle()}` : ` created a ${evt.target.getTitle()}`}
                    </span>
                </div>
            )
        }
    }

    // note or warning => use abstract generated title information
    let { message } = evt.props || {};
    if([ SystemEvent.actions.note, SystemEvent.actions.warning ].includes(evt.action.code)) {
        return {
            values: message ? [ message ] : [],
            title: evt.action.code === SystemEvent.actions.note ? 'Note' : 'Warning',
            components: (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: 8,
                    padding: '6px 12px 6px 12px'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        color: Appearance.colors.text(),
                        whiteSpace: 'normal'
                    }}>
                        {message || 'Message is no longer available'}
                    </span>
                </div>
            )
        }
    }

    // delete => use abstract generated title information
    if(evt.action.code === SystemEvent.actions.delete) {
        let val = `${evt.user.full_name} deleted the ${evt.target.getTitle()}`;
        return {
            values: [ val ],
            title: 'Deleted',
            components: (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: 8,
                    padding: '6px 12px 6px 12px'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        color: Appearance.colors.text(),
                        whiteSpace: 'normal'
                    }}>
                        <span style={{
                            fontWeight: 700,
                            color: Appearance.colors.text()
                        }}>
                            {evt.user.full_name}
                        </span>
                        {` deleted the ${evt.target.getTitle()}`}
                    </span>
                </div>
            )
        }
    }

    // check for original and current update properties
    let { original, current } = evt.props || {};
    if(!original || !current) {
        //console.log('missing original or current');
        return { values: [] };
    }

    // use key to create title for data point
    const getTitleFromKey = key => {

        // target specific data points
        switch(evt.target.type) {

            case 'appointments':
            switch(key) {
                case 'qualified':
                return 'Qualified';

                case 'primary_user':
                case 'user':
                return 'Closer';

                case 'feedback_template_id':
                return 'Feedback Template ID';
            }
            break;

            case 'leads':
            switch(key) {
                case 'spouse_first_name':
                return 'Spouse First Name';

                case 'spouse_last_name':
                return 'Spouse Last Name';

                case 'spouse_phone_number':
                return 'Spouse Phone Number';

                case 'spouse_email_address':
                return 'Email Address';

                case 'lead_type':
                return 'Lead Type';

                case 'lead_sub_type':
                return 'Lead Sub-Type';

                case 'lead_script':
                return 'Lead Script';

                case 'homeowner_status':
                return 'Homeowner Status';

                case 'marital_status':
                return 'Marital Status';

                case 'occupational_status':
                return 'Occupational Status';

                case 'user':
                return 'Lead Credit';

                case 'assignment_user':
                return 'Closer';

                case 'setter_user':
                return 'Setter';

                case 'opportunity':
                return 'Opportunity';

                case 'out_of_service_area':
                return 'Out of Service Area Flag';

                case 'priority':
                return 'Priority';

                case 'released':
                return 'Release';

                case 'notes':
                return 'Notes';

                case 'client':
                return 'Client';

                case 'tags':
                return 'Tags';

                case 'outof_service_area':
                return 'Out of Service Area';

                case 'active':
                return 'Archive Status';

                case 'sale_date':
                return 'Sale Date';
            }
        }

        // universal data points
        switch(key) {
            case 'id':
            return 'ID';

            case 'user_id':
            return 'User ID';

            case 'username':
            return 'Username';

            case 'first_name':
            return 'First Name';

            case 'last_name':
            return 'Last Name';

            case 'full_name':
            return 'Full Name';

            case 'phone_number':
            return 'Phone Number';

            case 'email_address':
            return 'Email Address';

            case 'address':
            return 'Address';

            case 'location':
            return 'Location Coordinates';

            case 'client':
            return 'Client';

            case 'client_id':
            return 'Client ID';

            case 'date':
            return 'Date';

            case 'start_date':
            return 'Start Date';

            case 'end_date':
            return 'End Date';

            case 'timezone':
            return 'Timezone';

            case 'status':
            return 'Status';

            case 'notes':
            return 'Notes';
        }
        return key || 'Unknown data point';
    }

    // check for specialty formatting based on data point key or value
    const getValue = (key, target) => {

        // target specific data points
        switch(evt.target.type) {
            case 'appointments':
            switch(key) {
                case 'qualified':
                return target[key] ? 'Yes' : 'No';

                case 'primary_user':
                case 'user':
                return target[key] ? target[key]?.full_name || 'Name not available' : null;
            }
            break;

            case 'leads':
            switch(key) {
                case 'lead_type':
                return target[key] ? target[key].text : null;

                case 'lead_sub_type':
                return target[key] ? target[key].text : null;

                case 'lead_script':
                return target[key] ? target[key].title : null;

                case 'assignment_user':
                case 'setter_user':
                case 'user':
                return target[key] ? target[key]?.full_name || 'Name not available' : null;

                case 'homeowner_status':
                case 'marital_status':
                case 'occupational_status': 
                if(typeof(target[key]) === 'string') {
                    return target[key];
                }
                if(target[key] && typeof(target[key]) === 'object' && target[key].text) {
                    return target[key].text;
                }
                return null;

                case 'opportunity':
                case 'priority':
                return target[key] ? 'Yes' : 'No';

                case 'notes':
                return target[key]

                case 'tags':
                return target[key] ? Utils.oxfordImplode(target[key].map(tag => tag.text)) : null;

                case 'out_of_service_area':
                return target[key] ? 'Yes' : 'No';

                case 'active':
                return target[key] ? 'No' : 'Yes'; // inverted to represent the "archived" data point

                case 'sale_date':
                return target[key] ? moment.utc(target[key]).tz(target.timezone).format('MM/DD/YYYY [at] h:mma z') : null;
            }
            break;
        }

        // universal data points
        switch(key) {
            case 'address':
            return target[key] ? Utils.formatAddress(target[key]) : null;

            case 'location':
            return target[key] ? Utils.formatLocation(target[key]) : null;

            case 'date':
            case 'start_date':
            case 'end_date':
            return target[key] ? moment.utc(target[key]).tz(target.timezone).format('MM/DD/YYYY [at] h:mma z') : null;

            case 'status':
            return target[key] ? target[key].text : null;

            case 'active':
            return  target[key] ? 'Yes' : 'No';
        }

        if(target[key] && typeof(target[key]) === 'object') {
            return 'Unsupported value';
        }
        return target[key];
    }

    // check for specific color based on data point or value
    const getValueColor = (key, val, target) => {

        // prevent moving forward if no value was provided
        if(!val) {
            return Appearance.colors.grey();
        }

        // return explicit color is there is one
        if(target[key] && target[key].color) {
            return target[key].color;
        }

        // set color based on active flag
        if(key === 'active') {
            switch(evt.target.type) {
                case 'lead':
                return val === 'Yes' ? Appearance.colors.red : Appearance.colors.green; // inverted to represent the "archived" data point

                default:
                return val === 'Yes' ? Appearance.colors.green : Appearance.colors.red;
            }
        }
        return null;
    }

    // create text overview for system event
    let keys = Object.keys(original);
    Object.keys(current).forEach(key => {
        if(!keys.includes(key)) {
            keys.push(key);
        }
    })
    let values = keys.filter(key => {
        let originalVal = getValue(key, original);
        let currentVal = getValue(key, current);
        return originalVal !== currentVal;
    }).map(key => {
        let title = getTitleFromKey(key);
        let originalVal = getValue(key, original);
        let currentVal = getValue(key, current);
        return `${evt.user.full_name} changed the ${title.toLowerCase()} from ${originalVal || 'no value'} to ${currentVal || 'no value'}`;
    })

    // create components with text overview of system event
    let components = keys.filter(key => {
        let originalVal = getValue(key, original);
        let currentVal = getValue(key, current);
        return originalVal !== currentVal;
    }).map((key, index) => {

        let originalVal = getValue(key, original);
        let currentVal = getValue(key, current);

        let title = getTitleFromKey(key);
        return (
            <div
            key={index}
            style={{
                ...Appearance.styles.unstyledPanel(),
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                marginBottom: 8,
                padding: '6px 12px 6px 12px',
                width: '100%'
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: Appearance.colors.text(),
                    whiteSpace: 'normal'
                }}>
                    <span style={{
                        color: Appearance.colors.text(),
                        fontWeight: 700
                    }}>
                        {evt.user.full_name}
                    </span>
                    {` changed the ${title.toLowerCase()} from `}
                    <span style={{
                        fontWeight: 700,
                        color: getValueColor(key, originalVal, original) || Appearance.colors.text()
                    }}>
                        {originalVal || 'no value'}
                    </span>
                    {` to `}
                    <span style={{
                        color: getValueColor(key, currentVal, current) || Appearance.colors.green,
                        fontWeight: 700
                    }}>
                        {currentVal || 'no value'}
                    </span>
                </span>
            </div>
        )
    })

    return {
        components: components,
        title: 'Updated',
        values: values || []
    }
}

export const onNotificationAction = (utils, notification, onClick) => {

    const onShowAppointmentDetails = async id => {
        try {
            let appointment = await Appointment.get(utils, id);
            utils.layer.open({
                abstract: Abstract.create({
                    object: appointment,
                    type: 'appointments'
                }),
                Component: AppointmentDetails,
                id: `appointment_details_${appointment.id}`,
                permissions: ['appointments.details']
            });
        } catch(e){
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onShowEventDetails = async id => {
        try {
            let event = await Event.get(utils, id);
            utils.layer.open({
                id: `event_details_${event.id}`,
                abstract: Abstract.create({
                    type: 'events',
                    object: event
                }),
                Component: EventDetails
            });
        } catch(e){
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this event. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onShowLeadDetails = async id => {
        try {
            let lead = await Lead.get(utils, id);
            utils.layer.open({
                abstract: Abstract.create({
                    object: lead,
                    type: 'leads'
                }),
                Component: LeadDetails,
                id: `lead_details_${lead.id}`,
                permissions: ['leads.details']
            });
        } catch(e){
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    // prepare buttons array
    let buttons = [];

    // determine if a payload was provided with resource details
    if(notification.payload) {

        // add support for viewing a appointment if applicable
        if(notification.payload.appointment_id) {
            buttons.push({
                key: 'appointment_id',
                title: 'View Appointment Details',
                style: 'default'
            });
        }

        // add support for viewing a appointment request if applicable
        if(notification.payload.appointment_request_id) {
            buttons.push({
                key: 'appointment_request_id',
                title: 'View Appointment Request Details',
                style: 'default'
            });
        }

        // add support for viewing a custom event if applicable
        if(notification.payload.event_id) {
            buttons.push({
                key: 'event_id',
                title: 'View Event Details',
                style: 'default'
            });
        }

        // add support for viewing a lead if applicable
        if(notification.payload.lead_id) {
            buttons.push({
                key: 'lead_id',
                title: 'View Lead Details',
                style: 'default'
            });
        }
    }

    // show alert with notification action buttons
    utils.alert.show({
        title: notification.title,
        message: notification.message,
        icon: {
            path: notification.from_user ? notification.from_user.avatar : 'images/push-notification-icon-white.png',
            style: {
                backgroundColor: getNotificationIconColor(notification)
            }
        },
        buttons: buttons.concat([{
            key: 'cancel',
            title: 'Okay',
            style: 'cancel'
        }]),
        onClick: async key => {
            if(typeof(onClick) === 'function') {
                onClick(notification, key);
            }
            switch(key) {
                case 'appointment_id':
                onShowAppointmentDetails(notification.payload.appointment_id);
                break;

                case 'event_id':
                onShowEventDetails(notification.payload.event_id);
                break;

                case 'lead_id':
                onShowLeadDetails(notification.payload.lead_id);
                break;
            }
        }
    });
}

export const reassignTasks = async (utils, userID, targets, onLoading) => {

    const setLoading = val => {
        if(typeof(onLoading) === 'function') {
            onLoading(val);
        }
    }
    const onConfirm = async (user, next_user, props) => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            let { count } = await Request.post(utils, '/clients/', {
                type: 'reassign_tasks',
                targets: targets,
                ...props
            });

            setLoading(false);
            utils.content.fetch(targets);
            utils.banner.show({
                title: 'Tasks Reassigned',
                message: `We have re-assigned ${count} ${count === 1 ? 'task' : 'tasks'} from ${user.full_name} to ${next_user.full_name}`
            })

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue re-assigning the tasks for ${user.full_name}. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    try {

        utils.alert.dev();
        return;

        let user = await User.get(utils, userID);
        let timezone = moment.tz.guess();
        let startDate = moment().startOf('day');
        let endDate = moment().endOf('day');
        let destinationUser = null;

        utils.alert.show({
            title: 'Re-Assign Tasks',
            message: `You can automatically re-assign all scheduled calls, emails, and Appointments for ${user.full_name} if they are out of office or unable to complete their assignments. Please choose a user to accept ${user.first_name}'s assignments and a date range of assignments to move over to the new user.`,
            content: (
                <div style={{
                    width: '100%',
                    padding: 12,
                    paddingTop: 0
                }}>
                    <LayerItem title={'Assign To'}>
                        <div style={{
                            ...Appearance.styles.unstyledPanel(),
                            padding: 12,
                            backgroundColor: Appearance.colors.transparent
                        }}>
                            <UserLookupField
                            utils={utils}
                            icon={'search'}
                            avatarClick={false}
                            placeholder={'Search by first or last name...'}
                            onChange={result => destinationUser = result} />
                        </div>
                    </LayerItem>

                    <LayerItem title={'Date Range'}>
                        <div style={{
                            ...Appearance.styles.unstyledPanel(),
                            padding: 12,
                            backgroundColor: Appearance.colors.transparent
                        }}>
                            <DateDurationPickerField
                            onChange={result => {
                                startDate = result.date;
                                timezone = result.timezone;
                            }}
                            overrideAlerts={true}
                            placeholder={'Choose a start date...'}
                            style={{
                                marginBottom: 8
                            }}
                            utils={utils}
                            value={startDate} />
                        </div>
                    </LayerItem>
                </div>
            ),
            buttons: [{
                key: 'done',
                title: 'Done',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'done' && destinationUser && startDate && endDate) {
                    onConfirm(user, destinationUser, {
                        user_id: userID,
                        destination_user_id: destinationUser.user_id,
                        start_date: startDate.format('YYYY-MM-DD HH:mm:ss'),
                        end_date: endDate.format('YYYY-MM-DD HH:mm:ss')
                    });
                    return;
                }
            }
        })

    } catch(e) {
        utils.alert.show({
            title: 'Oops!',
            message: `There was an issue retrieving the information for this account. ${e.message || 'An unknown error occurred'}`
        })
    }
}

export const unassignTasks = async (utils, userID, targets, onLoading) => {

    const setLoading = val => {
        if(typeof(onLoading) === 'function') {
            onLoading(val);
        }
    }
    const onConfirm = async (user, props) => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            let { count } = await Request.post(utils, '/clients/', {
                type: 'reassign_tasks',
                targets: targets,
                ...props
            });

            setLoading(false);
            utils.content.fetch(targets);
            utils.banner.show({
                title: 'Tasks Unassigned',
                message: `We have un-assigned ${count} ${count === 1 ? 'task' : 'tasks'} from ${user.full_name}`
            })

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue un-assigning the tasks for ${user.full_name}. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    try {

        utils.alert.dev();
        return;
        
        let user = await User.get(utils, userID);
        let startDate = moment().startOf('day');
        let endDate = moment().endOf('day');

        utils.alert.show({
            title: 'Un-Assign Tasks',
            message: `You can automatically un-assign all scheduled calls, emails, and Appointments for ${user.full_name} if they have left your office or unable to complete their assignments. This will open up all calls, emails, and Appointments to new assignments.`,
            content: (
                <div style={{
                    width: '100%',
                    padding: 12,
                    paddingTop: 0
                }}>
                    <LayerItem title={'Date Range'}>
                        <div style={{
                            ...Appearance.styles.unstyledPanel(),
                            padding: 12,
                            backgroundColor: Appearance.colors.transparent
                        }}>
                            <DateDurationPickerField
                            onChange={date => startDate = date}
                            overrideAlerts={true}
                            placeholder={'Choose a start date...'}
                            style={{
                                marginBottom: 8
                            }}
                            utils={utils}
                            value={startDate} />

                            <DateDurationPickerField
                            placeholder={'Choose an end date...'}
                            onChange={date => endDate = date} 
                            overrideAlerts={true}
                            utils={utils}
                            value={endDate} />
                        </div>
                    </LayerItem>
                </div>
            ),
            buttons: [{
                key: 'done',
                title: 'Done',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'done' && startDate && endDate) {
                    onConfirm(user, {
                        user_id: userID,
                        start_date: startDate.format('YYYY-MM-DD HH:mm:ss'),
                        end_date: endDate.format('YYYY-MM-DD HH:mm:ss')
                    });
                    return;
                }
            }
        })

    } catch(e) {
        utils.alert.show({
            title: 'Oops!',
            message: `There was an issue retrieving the information for this account. ${e.message || 'An unknown error occurred'}`
        })
    }
}

export const UserAvailabilityEditor = ({ abstract, index, options, utils }) => {

    const layerID = `user_availability_${abstract.getID()}`;
    const [availability, setAvailability] = useState([]);
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState('init');

    const onAddAvailabilityClick = async () => {
        try {

            // verify that required variables were provided
            await validateRequiredFields(getFields);

            // prepare target date using the timezone on file for the user
            let date = edits.is_recurring === true ? moment().startOf('week').add(edits.day_of_week, 'days') : moment(edits.specific_date);
            date = date.tz(edits.timezone);

            // prepare moments for start and end times
            let start = date.format('YYYY-MM-DD');
            let startMoment = moment(`${start} ${edits.start_time}`, 'YYYY-MM-DD HH:mm:ss');
            let endMoment = moment(`${start} ${edits.end_time}`, 'YYYY-MM-DD HH:mm:ss');

            // verify that start time is before end time
            if(startMoment.isSameOrAfter(endMoment)) {
                throw new Error('Start time must be before end time');
            }

            // prepare payload based on recurring vs specific
            let payload = {
                end_time: endMoment.format('HH:mm:ss'),
                is_recurring: edits.is_recurring,
                start_time: startMoment.format('HH:mm:ss'),
                timezone: edits.timezone,
                type: 'new_availability',
                user_id: abstract.getID()
            };

            // add day_of_week for recurring availability
            if(edits.is_recurring === true) {
                payload.day_of_week = edits.day_of_week;
            }
            
            // add specific_date for non-recurring availability
            if(edits.is_recurring === false) {
                payload.specific_date = moment(edits.specific_date).format('YYYY-MM-DD');
            }

            // set loading flag and submit to server
            console.log(payload);
            setLoading('submit');
            let { availability: record } = await Request.post(utils, '/users/', payload);

            // reset edits for next entry
            setEdits({ 
                end_time: moment().add(1, 'hours').format('HH:mm:ss'),
                is_recurring: false,
                specific_date: moment().format('YYYY-MM-DD'),
                start_time: moment().format('HH:mm:ss'),
                timezone: moment.tz.guess()
            });

            // add new availablity entry to list of entries 
            setAvailability(entries => {
                entries.push(UserAvailability.create(record));
                return entries.sort((a, b) => {
                    if(a.is_recurring && b.is_recurring) {
                        return a.day_of_week - b.day_of_week;
                    }
                    if(a.is_recurring) {
                        return -1;
                    }
                    if(b.is_recurring) {
                        return 1;
                    }
                    return moment(a.specific_date).diff(moment(b.specific_date));
                });
            });

            // end loading and show confirmation banner
            setLoading(false);
            utils.banner.show({
                title: 'Availability Added',
                message: 'The availability record has been successfully added.'
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue adding the availability record. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onDeleteAvailability = record => {
        utils.alert.show({
            title: 'Delete Availability',
            message: 'Are you sure you want to delete this availability record?',
            buttons: [{
                key: 'delete',
                title: 'Delete',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'delete') {
                    onDeleteAvailabilityConfirm(record);
                    return;
                }
            }
        });
    }

    const onDeleteAvailabilityConfirm = async record => {
        try {

            setLoading(`delete_${record.id}`);
            await Request.post(utils, '/users/', {
                id: record.id,
                type: 'delete_availability'
            });

            // remove entry from list of entries
            setLoading(false);
            setAvailability(availability.filter(item => item.id !== record.id));

            // show confirmation banner
            utils.banner.show({
                title: 'Availability Deleted',
                message: 'The availability record has been successfully deleted.'
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting the availability record. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            loading: loading === 'submit',
            onClick: onAddAvailabilityClick,
            text: 'Submit'
        }];
    }

    const getContent = () => {
        if(loading === 'init') {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: 100,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 40,
                        width: 40
                    }}/>
                </div>
            )
        }

        return (
            <div style={{
                width: '100%'
            }}>
                <AltFieldMapper
                fields={getFields()}
                utils={utils} />
                <LayerItem 
                collapsed={false}
                lastItem={true}
                title={'Existing Availability'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        <span style={{
                            borderBottom: `1px solid ${Appearance.colors.divider()}`,
                            color: Appearance.colors.text(),
                            display: 'block',
                            fontWeight: 500,
                            fontSize: 14,
                            padding: '12px 16px 12px 16px',
                            width: '100%'
                        }}>{'This section displays all active availability blocks for this closer. Any appointments can only be booked within the active time windows shown here. If no availability is listed, the closer will not appear as bookable on the calendar.'}</span>
                        {availability.length === 0 && (
                            Views.entry({
                                bottomBorder: false,
                                hideIcon: true,
                                subTitle: 'No availability set has been set for this user.',
                                title: 'No Availability Found'
                            })
                        )}
                        {availability.map((record, index) => {

                            // prepare moment targets for start and end time
                            let endTime = record.end_time;
                            let startTime = record.start_time;

                            return (
                                Views.entry({
                                    bottomBorder: index !== availability.length - 1,
                                    icon: {
                                        imageStyle: {
                                            borderRadius: 0
                                        },
                                        path: record.is_recurring ? (window.theme === 'dark' ? 'images/user-availability-recurring-dark.png' : 'images/user-availability-recurring-light.png') : (window.theme === 'dark' ? 'images/user-availability-dark.png' : 'images/user-availability-light.png')
                                    },
                                    key: index,
                                    loading: loading === `delete_${record.id}`,
                                    rightContent: (
                                        <img
                                        className={'text-button'}
                                        onClick={() => onDeleteAvailability(record)}
                                        src={'images/delete-red-small.png'}
                                        style={{
                                            height: 25,
                                            marginLeft: 12,
                                            width: 25
                                        }} />
                                    ),
                                    subTitle: `${startTime.format('h:mmA')} - ${endTime.format('h:mmA')}`,
                                    title: record.is_recurring ? `Every ${UserAvailability.dayOfWeekToText(record.day_of_week)}` : record.specific_date.format('MM/DD/YYYY')
                                })
                            )
                        })}
                    </div>
                </LayerItem>
            </div>
        );
    }

    const getDayItems = () => {
        return [{ 
            id: 0, 
            title: 'Sunday' 
        },{ 
            id: 1, 
            title: 'Monday' 
        },{ 
            id: 2, 
            title: 'Tuesday' 
        },{ 
            id: 3, 
            title: 'Wednesday' 
        },{ 
            id: 4, 
            title: 'Thursday' 
        },{ 
            id: 5, 
            title: 'Friday' 
        },{ 
            id: 6, 
            title: 'Saturday' 
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            description: 'Set the specific dates and time windows when this closer is available to take appointments. The system uses this availability to automatically determine when meetings can be scheduled. Only times entered here will appear as booking options.',
            id: 'new',
            lastItem: false,
            title: 'Add New Availability',
            items: [{
                component: 'list',
                items: [{ 
                    id: 'recurring', 
                    title: 'Recurring (Weekly)' 
                },{ 
                    id: 'non_recurring', 
                    title: 'Specific Date' 
                }],
                key: 'is_recurring',
                onChange: item => {
                    onUpdateTarget({ 
                        day_of_week: item.id === 'recurring' ? 1 : null,
                        is_recurring: item.id === 'recurring',
                        specific_date: item.id === 'recurring' ? null : moment().format('YYYY-MM-DD')
                    });
                },
                title: 'Type',
                value: edits.is_recurring ? 'Recurring (Weekly)' : 'Specific Date'
            },{
                component: 'timezone_picker',
                key: 'timezone',
                onChange: result => onUpdateTarget({ timezone: result }),
                title: 'Timezone',
                value: edits.timezone
            },{
                component: 'list',
                items: getDayItems(),
                key: 'day_of_week',
                onChange: item => onUpdateTarget({ day_of_week: item.id }),
                required: edits.is_recurring === true,
                title: 'Day of Week',
                value: Number.isInteger(edits.day_of_week) ? UserAvailability.dayOfWeekToText(edits.day_of_week) : null,
                visible: edits.is_recurring === true
            },{
                component: 'date_picker_dropdown',
                key: 'specific_date',
                onChange: date => onUpdateTarget({ specific_date: date ? date.format('YYYY-MM-DD') : null }),
                required: edits.is_recurring === false,
                title: 'Specific Date',
                value: edits.specific_date,
                visible: edits.is_recurring === false
            },{
                component: 'time_picker',
                key: 'start_time',
                onChange: val => onUpdateTarget({ start_time: val?.format('HH:mm:ss') }),
                placeholder: 'Start Time',
                props: {
                    increments: 30
                },
                title: 'Start Time',
                value: edits.start_time
            },{
                component: 'time_picker',
                key: 'end_time',
                onChange: val => onUpdateTarget({ end_time: val?.format('HH:mm:ss') }),
                placeholder: 'End Time',
                props: {
                    increments: 30
                },
                title: 'End Time',
                value: edits.end_time
            }]
        }]
    }

    const setupTarget = async () => {
        try {

            // setup default values for edits
            let now = moment().endOf('hour');
            setEdits({
                end_time: now.clone().add(1, 'hours').format('HH:mm:ss'),
                is_recurring: false,
                specific_date: now.format('YYYY-MM-DD'),
                start_time: now.format('HH:mm:ss'),
                timezone: moment.tz.guess()
            });

            // set loading flag and fetch existing availability records for this user
            let { availability } = await Request.get(utils, '/users/', {
                type: 'all_availability',
                user_id: abstract.getID()
            });

            // end loading and update local state
            setLoading(false);
            setAvailability(availability.map(entry => UserAvailability.create(entry)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading availability for this user. ${e.message || 'An unknown error occurred'}`,
                onClick: () => setLayerState('close')
            });
        }
    }

    useEffect(() => {
        setTimeout(setupTarget, 250);
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={`${abstract.object.full_name} - Availability`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            {getContent()}
        </Layer>
    )
}

export const UserAvailabilityPanel = ({ index, options, utils }) => {

    const panelID = 'user_availability';
    const limit = 10;
    const offset = useRef(0);
    const sorting = useRef({ 
        sort_key: 'created', 
        sort_type: Content.sorting.type.descending 
    });

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const [users, setUsers] = useState([]);
    const [searchText, setSearchText] = useState(null);

    const onEntryClick = entry => {
        
        // find matching user for entry and open editor
        let user = users.find(user => user.user_id === entry.user_id);
        if(user) {
            utils.layer.open({
                id: `user_availability_editor_${user.user_id}`,
                abstract: Abstract.create({
                    object: user,
                    type: 'users'
                }),
                Component: UserAvailabilityEditor,
                permissions: ['users.edit']
            });
        }
    }

    const getContent = () => {
        if(entries.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: `No availability entries were found in the system`,
                    title: `No Availability Found`
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {entries.map(getFields)}
                </tbody>
            </table>
        )
    }

    const getFields = (entry, index) => {

        let target = entry || {};
        let fields = [{
            key: 'full_name',
            title: 'Name',
            icon: target.user?.avatar,
            value: target.user?.full_name
        },{
            key: 'created',
            title: 'Created',
            value: target.created ? Utils.formatDate(target.created) : null
        },{
            key: 'is_recurring',
            title: 'Recurring',
            value: target.is_recurring ? 'Yes' : 'No'
        },{
            key: 'day_of_week',
            title: 'Day of Week',
            value: target.day_of_week ? UserAvailability.dayOfWeekToText(target.day_of_week) : null
        },{
            key: 'specific_date',
            title: 'Specific Date',
            value: target.specific_date ? target.specific_date.format('MM/DD/YYYY') : null
        },{
            key: 'start_time',
            title: 'Start Time',
            value: target.start_time ? target.start_time.format('hh:mmA') : null
        },{
            key: 'end_time',
            title: 'End Time',
            value: target.end_time ? target.end_time.format('hh:mmA') : null
        }];

        // create table headers with custom sorting options
        if(!entry) {
            return (
                <TableListHeader
                fields={fields}
                onChange={props => {
                    sorting.current = props;
                    fetchEntries();
                }}
                value={sorting.current}/>
            )
        }

        // loop through result rows
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: index !== entries.length - 1 && `1px solid ${Appearance.colors.divider()}`
            }}>
                {fields.map((field, fieldIndex) => {
                    return (
                        <td
                        key={fieldIndex}
                        className={'pl-2 pr-3 py-2 flexible-table-column'}
                        onClick={onEntryClick.bind(this, entry)}>
                            <div style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row'
                            }}>
                                {typeof(field.icon) === 'string' && (
                                    <img
                                    src={field.icon}
                                    style={{
                                        borderRadius: 12.5,
                                        height: 25,
                                        minHeight: 25,
                                        minWidth: 25,
                                        objectFit: 'cover',
                                        overflow: 'hidden',
                                        marginRight: 8,
                                        width: 25
                                    }}/>
                                )}
                                {typeof(field.value) === 'string' && (
                                    <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                                )}
                                {typeof(field.value) !== 'string' && field.value}
                            </div>
                        </td>
                    )
                })}
            </tr>
        )
    }


    const fetchEntries = async () => {
        try {

            // fetch availability for all users  
            setLoading(true);
            let { entries, paging, users } = await Request.get(utils, '/clients/', {
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                type: 'availability_overview',
                ...sorting.current
            });

            // end loading and update local states
            setLoading(false);
            setEntries(entries.map(entry => UserAvailability.create(entry)));
            setPaging(paging);
            setUsers(users.map(user => User.create(user)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading availability list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchEntries();
    }, [searchText]);

    useEffect(() => {
        utils.content.subscribe(panelID, ['user_availability'], {
            onUpdate: fetchEntries
        });
        return () => {
            utils.content.unsubscribe(panelID);
        }
    }, []);

    return (
        <Panel
        id={panelID}
        index={index}
        title={'User Availability'}
        utils={utils}
        options={{
            ...options,
            loading: loading === true,
            paging: {
                data: paging,
                limit: limit,
                offset: offset.current,
                onClick: next => {
                    offset.current = next;
                    fetchEntries();
                }
            },
            search: {
                placeholder: 'Search by first and last name...',
                onChange: text => {
                    offset.current = 0;
                    setSearchText(text);
                }
            },
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}
